// src/components/Entity/WizardAddEntity.js
// Wizard complet pour création société - Version expert-comptable
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  LEGAL_FORMS,
  TAX_REGIMES,
  ACTIVITY_TYPES,
  COMMON_APE_CODES,
  SOURCE_TYPES,
  COMMON_CHARGE_LINES,
  MANAGER_TYPES,
  PCG_PRODUITS,
  PCG_CHARGES,
  PRODUIT_TYPES,
  getSourceTypeInfo,
  getLegalFormInfo,
  getManagerTypeForLegalForm,
  calculateManagerSocialCharges,
  calculateChargesFromBrut,
  getPCGProduit,
  getPCGCharge,
} from '../../utils/entityConstants';
import ExerciceDetailModal from './ExerciceDetailModal';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../../hooks/useConfirm';
import './WizardAddEntity.css';

// Composant Tooltip propre avec Portal (évite les problèmes de overflow)
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 10, // Au-dessus du trigger
        left: rect.left + rect.width / 2 // Centré
      });
    }
    setShow(true);
  };

  return (
    <span
      ref={triggerRef}
      className="tooltip-trigger"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && ReactDOM.createPortal(
        <div
          className="tooltip-portal"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 999999
          }}
        >
          <div className="tooltip-content-portal">{text}</div>
        </div>,
        document.body
      )}
    </span>
  );
};

const STEPS = [
  { id: 'identity', label: 'Identité', icon: '1' },
  { id: 'charges', label: 'Charges', icon: '2' },
  { id: 'ca', label: 'Revenus', icon: '3' },
  { id: 'summary', label: 'Résumé', icon: '4' }
];

const WizardAddEntity = ({ onSave, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  // ==================== STATE ====================

  // Step 1: Identity - PAS de présélection
  const [entityData, setEntityData] = useState({
    name: '',
    legalForm: '',
    taxRegime: '',
    activityType: '',
    apeCode: '',
    fiscalYearEnd: '',
    fiscalYearEndCustom: '',
    firstExerciceType: 'long', // 'short' | 'long' - type de premier exercice
    shareCapital: '', // Pas de pré-remplissage
    isGérantMajoritaire: true, // Pour SARL
    creationDate: '',
    postalCode: '', // Pour calcul CFE (détermine la commune)
  });

  // Ville détectée automatiquement depuis le code postal
  const [detectedCity, setDetectedCity] = useState('');

  // Step 2: Charges - Mode simplifié vs détaillé (pour Personnel + Exploitation uniquement)
  const [chargeMode, setChargeMode] = useState('simplified'); // 'simplified' | 'detailed'

  // Multi-dirigeants : tableau adapté selon forme juridique
  // SAS/SA : Président + optionnel DG (tous assimilé salarié)
  // SARL : Gérant(s) avec % parts (TNS si majoritaires collectivement)
  // SCI/SNC : Gérant unique (TNS)
  // Note: SASU/EURL déterminés automatiquement par le nombre d'actionnaires
  const [managers, setManagers] = useState([
    {
      id: 1,
      role: 'president', // 'president' | 'dg' | 'gerant' | 'co-gerant'
      netMonthly: 3000,
      months: 12,
      ownershipPercent: null, // Uniquement pour SARL (% parts)
      entryDate: null, // Date de nomination (locked for first Président/Gérant)
      salaryStartDate: null, // Date début rémunération (editable, default = creation date)
    }
  ]);

  // ACRE (réduction charges sociales première année) - applicable à tous les dirigeants
  const [hasACRE, setHasACRE] = useState(false);

  // Options générales
  const [hasCGA, setHasCGA] = useState(false); // Centre de Gestion Agréé
  const [tvaRegime, setTvaRegime] = useState('non_assujetti'); // 'non_assujetti' | 'reel_simplifie' | 'reel_normal'

  // Employés - Mode simplifié (nombre × salaire moyen × mois)
  const [hasEmployees, setHasEmployees] = useState(false);
  const [employeesData, setEmployeesData] = useState({
    count: 1,
    avgGrossSalary: 2500,
    months: 12,
  });

  // Employés - Mode détaillé (tableau de profils)
  const [employeeProfiles, setEmployeeProfiles] = useState([]);

  // Paramètres fiscaux CFE/CVAE - hypothèses éditables
  const [fiscalParams, setFiscalParams] = useState({
    // CFE: 2 modes - avec ou sans local
    cfeHasLocal: true, // true = avec local, false = sans local (base minimale)
    // Mode "avec local": valeur locative = surface × tarif cadastral
    cfeSurface: 50, // Surface des locaux en m²
    cfeTarifM2: 15, // Tarif cadastral moyen au m² (10-25€ selon zone)
    // Mode "sans local": base minimale fixée par commune (selon CA)
    cfeBaseMinimale: null, // null = auto selon CA, sinon valeur manuelle
    // Taux communal (s'applique dans les 2 cas)
    cfeTaux: 25, // Taux communal en % (20-40% selon commune)
    // CVAE: % de valeur ajoutée par rapport au CA (hypothèse)
    cvaeVAPercent: 30, // VA = 30% du CA en moyenne
  });

  // Calcul base minimale CFE selon CA (barème 2026, valeurs médianes)
  // Source: https://entreprendre.service-public.gouv.fr/vosdroits/F23547
  const getCfeBaseMinimaleFromCA = (ca) => {
    if (ca > 500000) return 3958;   // 247-7669 → médiane ~3958
    if (ca > 250000) return 3072;   // 247-5897 → médiane ~3072
    if (ca > 100000) return 2188;   // 247-4129 → médiane ~2188
    if (ca > 32600) return 1362;    // 247-2477 → médiane ~1362
    if (ca > 10000) return 713;     // 247-1179 → médiane ~713
    if (ca > 5000) return 418;      // 247-589 → médiane ~418
    return 247;                      // Minimum légal
  };

  // Base imposable CFE selon le mode choisi
  const cfeBaseImposable = fiscalParams.cfeHasLocal
    ? fiscalParams.cfeSurface * fiscalParams.cfeTarifM2 // Avec local: valeur locative
    : (fiscalParams.cfeBaseMinimale || 0); // Sans local: base minimale

  // Pour compatibilité, on garde cfeEstimate calculé
  const cfeEstimate = Math.round(cfeBaseImposable * fiscalParams.cfeTaux / 100);

  // Charges en mode simplifié (% CA ou montant fixe)
  const [simplifiedCharges, setSimplifiedCharges] = useState({
    totalPercent: 20, // % du CA par défaut
    totalFixed: 25000,   // ou montant fixe par défaut (moyenne PME)
    usePercent: false,
  });

  // Charges en mode détaillé - pré-rempli avec valeurs moyennes
  const [detailedCharges, setDetailedCharges] = useState(() => {
    const initial = {};
    // Postes courants pré-activés par défaut
    const defaultEnabled = ['loyer', 'comptable', 'assurances', 'internet_tel', 'transport', 'fournitures'];
    COMMON_CHARGE_LINES.forEach(line => {
      initial[line.key] = {
        enabled: defaultEnabled.includes(line.key),
        value: line.defaultValue,
        type: line.type,
      };
    });
    return initial;
  });

  // Options avancées (Mère-fille retiré → géré via Shareholding)
  const [advancedOptions, setAdvancedOptions] = useState({
    jei: false,
    cir: false,
    cirAmount: 0,
  });

  // Dividendes
  const [dividendPercent, setDividendPercent] = useState(60);
  const [dividendPaymentDelay, setDividendPaymentDelay] = useState(6); // Mois après clôture

  // Step 3: Revenus - Nouvelle structure
  // Dates de prévision pour générer les exercices automatiquement
  const [forecastDates, setForecastDates] = useState({
    startDate: '', // sera pré-rempli avec la date de création
    endDate: '' // l'utilisateur doit le saisir
  });

  // Données par exercice : charges globales + CA principal + CA secondaires
  const [exerciceData, setExerciceData] = useState({});
  // Structure: { '2025': { chargesGlobales: {...}, caPrincipal: { amount, chargesVariables }, caSecondaires: [...] } }

  // Quel modal est ouvert
  const [activeModal, setActiveModal] = useState(null);
  // Structure: { type: 'chargesGlobales' | 'caPrincipal' | 'caSecondaire', exerciceKey, sourceIndex? }

  // Pour compatibilité avec le code existant (exercice detail modal)
  const [caSources, setCaSources] = useState([]);
  const [expandedSourceId, setExpandedSourceId] = useState(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // APE search
  const [apeSearch, setApeSearch] = useState('');
  const [showApeDropdown, setShowApeDropdown] = useState(false);

  // Pour le modal détail exercice
  const [selectedExercice, setSelectedExercice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exerciceOverrides, setExerciceOverrides] = useState({}); // { '2024': { charges... }, '2025': {...} }

  // State pour le champ actif (highlight dynamique)
  const [activeField, setActiveField] = useState('name'); // Champ actif pour highlight

  // State pour le warning IS irréversible (SCI/SNC)
  const [showISWarning, setShowISWarning] = useState(false);
  const [isWarningConfirmed, setIsWarningConfirmed] = useState(false);

  // Confirm modal state (for alerts)
  const { confirmState, showConfirm, hideConfirm, handleConfirm } = useConfirm();

  // Fonction pour déterminer quel champ doit être highlighted (prochain à remplir)
  const getActiveField = useCallback(() => {
    if (!entityData.name || entityData.name.trim() === '') return 'name';
    if (!entityData.creationDate) return 'creationDate';
    if (!entityData.postalCode || entityData.postalCode.trim() === '') return 'postalCode';
    if (!entityData.legalForm) return 'legalForm';
    if (!entityData.taxRegime) return 'taxRegime';
    if (!entityData.activityType) return 'activityType';
    if (!entityData.shareCapital && entityData.shareCapital !== 0) return 'shareCapital';
    if (!entityData.fiscalYearEnd && entityData.fiscalYearEnd !== 'custom') return 'fiscalYearEnd';
    return null; // Tous remplis
  }, [entityData]);

  // Helper pour savoir si un champ est rempli (pour afficher en jaune)
  const isFieldFilled = useCallback((fieldName) => {
    switch (fieldName) {
      case 'name': return entityData.name && entityData.name.trim() !== '';
      case 'creationDate': return !!entityData.creationDate;
      case 'postalCode': return entityData.postalCode && entityData.postalCode.trim() !== '';
      case 'legalForm': return !!entityData.legalForm;
      case 'taxRegime': return !!entityData.taxRegime;
      case 'activityType': return !!entityData.activityType;
      case 'shareCapital': return entityData.shareCapital !== '' && entityData.shareCapital !== null;
      case 'fiscalYearEnd': return !!entityData.fiscalYearEnd;
      default: return false;
    }
  }, [entityData]);

  // Mettre à jour activeField quand entityData change
  useEffect(() => {
    setActiveField(getActiveField());
  }, [getActiveField]);

  // Sync bidirectionnel: creationDate ↔ forecastDates.startDate
  // 1. creationDate → startDate (initialisation)
  useEffect(() => {
    if (entityData.creationDate && !forecastDates.startDate) {
      // creationDate est déjà au format YYYY-MM-DD
      setForecastDates(prev => ({
        ...prev,
        startDate: entityData.creationDate
      }));
    }
  }, [entityData.creationDate, forecastDates.startDate]);

  // 2. startDate → creationDate (quand l'utilisateur modifie Début en Step 3)
  useEffect(() => {
    if (forecastDates.startDate) {
      // startDate est maintenant au format YYYY-MM-DD
      if (entityData.creationDate !== forecastDates.startDate) {
        setEntityData(prev => ({ ...prev, creationDate: forecastDates.startDate }));
      }
    }
  }, [forecastDates.startDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Détecter automatiquement la ville depuis le code postal (API gouvernement)
  useEffect(() => {
    const fetchCity = async () => {
      if (entityData.postalCode && entityData.postalCode.length === 5) {
        try {
          const response = await fetch(
            `https://api-adresse.data.gouv.fr/search/?q=${entityData.postalCode}&type=municipality&limit=1`
          );
          const data = await response.json();
          if (data.features && data.features.length > 0) {
            setDetectedCity(data.features[0].properties.city || data.features[0].properties.label);
          } else {
            setDetectedCity('');
          }
        } catch (error) {
          console.error('Erreur API adresse:', error);
          setDetectedCity('');
        }
      } else {
        setDetectedCity('');
      }
    };
    fetchCity();
  }, [entityData.postalCode]);

  // ==================== DERIVED DATA ====================

  const legalFormInfo = useMemo(() => getLegalFormInfo(entityData.legalForm), [entityData.legalForm]);

  // Déterminer si on peut ajouter des dirigeants supplémentaires
  const canAddManager = useMemo(() => {
    const form = entityData.legalForm;
    // SAS/SA : peut ajouter DG (SASU déterminé par shareholding)
    if (['SAS', 'SA'].includes(form)) {
      return !managers.some(m => m.role === 'dg');
    }
    // SARL : peut ajouter co-gérants (EURL déterminé par shareholding)
    if (form === 'SARL') {
      return true;
    }
    // SCI/SNC : un seul gérant
    return false;
  }, [entityData.legalForm, managers]);

  // Label du bouton d'ajout selon forme juridique
  const addManagerLabel = useMemo(() => {
    const form = entityData.legalForm;
    if (['SAS', 'SA'].includes(form)) return '+ Ajouter DG';
    if (form === 'SARL') return '+ Ajouter co-gérant';
    return null;
  }, [entityData.legalForm]);

  // Check if a manager is the first mandatory manager (entry date locked to creation date)
  const isFirstMandatoryManager = useCallback((manager) => {
    const form = entityData.legalForm;
    const isPresident = manager.role === 'president';
    const isGerant = manager.role === 'gerant';

    if ((form === 'SAS' || form === 'SASU') && isPresident) {
      // First Président in the list
      const presidents = managers.filter(m => m.role === 'president');
      return presidents.length > 0 && presidents[0].id === manager.id;
    }
    if ((form === 'SARL' || form === 'EURL') && isGerant) {
      // First Gérant in the list
      const gerants = managers.filter(m => m.role === 'gerant');
      return gerants.length > 0 && gerants[0].id === manager.id;
    }
    return false;
  }, [entityData.legalForm, managers]);

  // Calcul du statut de chaque dirigeant
  const managersWithStatus = useMemo(() => {
    const form = entityData.legalForm;

    // Pour SARL, calculer si gérance majoritaire collective
    if (form === 'SARL') {
      const totalParts = managers.reduce((sum, m) => sum + (m.ownershipPercent || 0), 0);
      const isCollectivelyMajority = totalParts > 50;

      return managers.map(m => ({
        ...m,
        status: isCollectivelyMajority ? 'TNS' :
                (m.ownershipPercent > 50 ? 'TNS' : 'ASSIMILE_SALARIE'),
        statusLabel: isCollectivelyMajority ? 'TNS (gérance majoritaire)' :
                     (m.ownershipPercent > 50 ? 'TNS' : 'Assimilé salarié'),
      }));
    }

    // Pour autres formes, statut déterminé par la forme juridique
    const defaultStatus = getManagerTypeForLegalForm(form, entityData.isGérantMajoritaire);
    const statusLabel = MANAGER_TYPES[defaultStatus]?.label || defaultStatus;

    return managers.map(m => ({
      ...m,
      status: defaultStatus,
      statusLabel,
    }));
  }, [managers, entityData.legalForm, entityData.isGérantMajoritaire]);

  // Premier dirigeant pour compatibilité
  const primaryManager = managersWithStatus[0] || { netMonthly: 0, months: 12, status: 'AUCUN' };
  const managerType = primaryManager.status;
  const managerTypeInfo = useMemo(() => MANAGER_TYPES[managerType] || MANAGER_TYPES.AUCUN, [managerType]);

  // Helper: calcule le prorata en mois depuis des dates précises (jour inclus)
  const calculateMonthsProrata = useCallback((startDate, endDate, isOngoing) => {
    if (isOngoing || !startDate) return 12;
    if (!endDate) return 12;

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 12;

    // Calcul en jours puis conversion en mois (30.42 jours/mois en moyenne)
    const diffDays = Math.max(0, (end - start) / (1000 * 60 * 60 * 24) + 1);
    const monthsWorked = Math.min(12, diffDays / 30.42);
    return Math.round(monthsWorked * 100) / 100; // Arrondi à 2 décimales
  }, []);

  // Calcul coût total employés - Mode simplifié
  const employeesTotalCost = useMemo(() => {
    if (!hasEmployees) return 0;
    if (chargeMode === 'detailed' && employeeProfiles.length > 0) {
      // Mode détaillé : somme des profils avec prorata jour précis
      return employeeProfiles.reduce((total, profile) => {
        const monthsWorked = calculateMonthsProrata(profile.startDate, profile.endDate, profile.isOngoing);
        const annualGross = profile.grossMonthlySalary * monthsWorked * profile.headcount;
        return total + Math.round(annualGross * 1.45);
      }, 0);
    }
    // Mode simplifié
    const masseSalarialeBrute = employeesData.count * employeesData.avgGrossSalary * employeesData.months;
    return Math.round(masseSalarialeBrute * 1.45);
  }, [hasEmployees, employeesData, chargeMode, employeeProfiles, calculateMonthsProrata]);

  // Calcul charges sociales pour chaque dirigeant
  const managersChargesCalc = useMemo(() => {
    return managersWithStatus.map(manager => {
      const annualNet = manager.netMonthly * manager.months;
      const baseCalc = calculateManagerSocialCharges(annualNet, manager.status);

      return {
        ...manager,
        annualNet,
        charges: baseCalc.charges,
        cout: baseCalc.cout,
      };
    });
  }, [managersWithStatus]);

  // Total coût dirigeants
  const totalManagersCost = useMemo(() => {
    return managersChargesCalc.reduce((sum, m) => sum + (m.cout || 0), 0);
  }, [managersChargesCalc]);

  // Compatibilité avec l'ancien code
  const managerChargesCalc = useMemo(() => {
    const first = managersChargesCalc[0];
    if (!first) return { charges: 0, cout: 0, net: 0 };

    if (hasACRE) {
      return {
        ...first,
        charges: first.charges * 0.5,
        cout: first.annualNet + (first.charges * 0.5),
        acreApplied: true
      };
    }
    return first;
  }, [managersChargesCalc, hasACRE]);

  // Compatibilité - pour l'ancien code qui utilise managerRemuneration
  const managerRemuneration = primaryManager;

  // Total charges d'exploitation (hors dirigeant)
  const totalExploitationCharges = useMemo(() => {
    if (chargeMode === 'simplified') {
      // En mode simplifié, on retourne juste la valeur
      return {
        fixed: simplifiedCharges.usePercent ? null : (simplifiedCharges.totalFixed || 0),
        percent: simplifiedCharges.usePercent ? (simplifiedCharges.totalPercent || 20) : null,
      };
    } else {
      // Mode détaillé: somme des lignes activées
      let totalFixed = 0;
      let totalPercent = 0;
      Object.entries(detailedCharges).forEach(([key, data]) => {
        if (data.enabled) {
          if (data.type === 'fixed') {
            totalFixed += data.value || 0;
          } else {
            totalPercent += data.value || 0;
          }
        }
      });
      return { fixed: totalFixed, percent: totalPercent };
    }
  }, [chargeMode, simplifiedCharges, detailedCharges]);

  // Fiscal year end
  const fiscalYearEnd = entityData.fiscalYearEnd === 'custom'
    ? entityData.fiscalYearEndCustom
    : entityData.fiscalYearEnd;

  // APE codes filtrés
  const filteredApeCodes = useMemo(() => {
    if (!apeSearch) return COMMON_APE_CODES;
    const search = apeSearch.toLowerCase();
    return COMMON_APE_CODES.filter(c =>
      c.code.toLowerCase().includes(search) ||
      c.label.toLowerCase().includes(search)
    );
  }, [apeSearch]);

  // ==================== HANDLERS ====================

  const handleEntityChange = (field, value) => {
    setEntityData(prev => ({ ...prev, [field]: value }));

    // Si changement de forme juridique, réinitialiser les dirigeants et régime fiscal
    if (field === 'legalForm') {
      const formInfo = getLegalFormInfo(value);
      const defaultRole = ['SAS', 'SA'].includes(value) ? 'president' : 'gerant';
      setManagers([{
        id: 1,
        role: defaultRole,
        netMonthly: 3000,
        months: 12,
        ownershipPercent: value === 'SARL' ? 100 : null,
      }]);
      // Auto-set default tax regime for this legal form
      if (formInfo && formInfo.defaultTaxRegime) {
        setEntityData(prev => ({ ...prev, [field]: value, taxRegime: formInfo.defaultTaxRegime }));
      }
      // Reset IS warning state when changing legal form
      setIsWarningConfirmed(false);
    }
  };

  // Handler spécifique pour le régime fiscal avec warning pour SCI/SNC + IS
  const handleTaxRegimeChange = (value) => {
    const isCivilCompany = ['SCI', 'SNC'].includes(entityData.legalForm);

    // Si SCI/SNC sélectionne IS, montrer le warning
    if (isCivilCompany && value === 'IS') {
      setShowISWarning(true);
      return; // Ne pas appliquer le changement immédiatement
    }

    // Sinon appliquer directement
    handleEntityChange('taxRegime', value);
  };

  // Confirmer le passage IS pour SCI/SNC
  const confirmISSelection = () => {
    if (isWarningConfirmed) {
      handleEntityChange('taxRegime', 'IS');
      setShowISWarning(false);
      setIsWarningConfirmed(false);
    }
  };

  // Annuler le passage IS
  const cancelISSelection = () => {
    setShowISWarning(false);
    setIsWarningConfirmed(false);
  };

  // Handlers pour les dirigeants
  const handleManagerChange = (managerId, field, value) => {
    setManagers(prev => prev.map(m =>
      m.id === managerId ? { ...m, [field]: value } : m
    ));
  };

  const addManager = () => {
    const form = entityData.legalForm;
    let newRole = 'gerant';

    if (['SAS', 'SA'].includes(form)) {
      newRole = 'dg';
    } else if (form === 'SARL') {
      newRole = 'co-gerant';
    }

    setManagers(prev => [...prev, {
      id: Date.now(),
      role: newRole,
      netMonthly: 0,
      months: 12,
      ownershipPercent: form === 'SARL' ? 0 : null,
      entryDate: null, // Editable for additional managers
      salaryStartDate: null, // Default = creation date
    }]);
  };

  const removeManager = (managerId) => {
    const manager = managers.find(m => m.id === managerId);
    if (!manager) return;

    const form = entityData.legalForm;

    // Cannot delete only Président in SAS/SASU
    if ((form === 'SAS' || form === 'SASU') &&
        manager.role === 'president' &&
        managers.filter(m => m.role === 'president').length === 1) {
      showConfirm({
        title: 'Impossible de supprimer',
        message: 'Une SAS/SASU doit avoir au moins un Président.',
        confirmText: 'OK',
        cancelText: '',
        type: 'warning',
        onConfirm: () => {}
      });
      return;
    }

    // Cannot delete only Gérant in SARL/EURL
    if ((form === 'SARL' || form === 'EURL') &&
        (manager.role === 'gerant' || manager.role === 'co-gerant') &&
        managers.filter(m => m.role === 'gerant' || m.role === 'co-gerant').length === 1) {
      showConfirm({
        title: 'Impossible de supprimer',
        message: 'Une SARL/EURL doit avoir au moins un Gérant.',
        confirmText: 'OK',
        cancelText: '',
        type: 'warning',
        onConfirm: () => {}
      });
      return;
    }

    setManagers(prev => prev.filter(m => m.id !== managerId));
  };

  // Handlers pour les profils employés (mode détaillé)
  const addEmployeeProfile = () => {
    setEmployeeProfiles(prev => [...prev, {
      id: Date.now(),
      profileName: '',
      grossMonthlySalary: 2500,
      startDate: entityData.creationDate || '',
      endDate: '',
      isOngoing: true,
      headcount: 1,
    }]);
  };

  const updateEmployeeProfile = (profileId, field, value) => {
    setEmployeeProfiles(prev => prev.map(p =>
      p.id === profileId ? { ...p, [field]: value } : p
    ));
  };

  const removeEmployeeProfile = (profileId) => {
    setEmployeeProfiles(prev => prev.filter(p => p.id !== profileId));
  };

  const handleDetailedChargeToggle = (key) => {
    setDetailedCharges(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled }
    }));
  };

  const handleDetailedChargeChange = (key, field, value) => {
    setDetailedCharges(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value }
    }));
  };

  // Validation Step 1 - Tous champs obligatoires
  const isStep1Valid = useMemo(() => {
    // Nom non vide
    if (!entityData.name.trim()) return false;
    // Date de création renseignée
    if (!entityData.creationDate) return false;
    // Forme juridique sélectionnée (toujours vrai car valeur par défaut)
    if (!entityData.legalForm) return false;
    // Clôture exercice définie
    const hasValidFiscalYearEnd = entityData.fiscalYearEnd !== 'custom' ||
      (entityData.fiscalYearEnd === 'custom' && entityData.fiscalYearEndCustom);
    if (!hasValidFiscalYearEnd) return false;
    return true;
  }, [entityData.name, entityData.creationDate, entityData.legalForm, entityData.fiscalYearEnd, entityData.fiscalYearEndCustom]);
  // Validation Step 2: Mandatory managers check
  const isStep2Valid = useMemo(() => {
    const form = entityData.legalForm;
    // SAS/SASU must have at least one Président
    if ((form === 'SAS' || form === 'SASU') && !managers.some(m => m.role === 'president')) {
      return false;
    }
    // SARL/EURL must have at least one Gérant
    if ((form === 'SARL' || form === 'EURL') && !managers.some(m => m.role === 'gerant' || m.role === 'co-gerant')) {
      return false;
    }
    return true;
  }, [entityData.legalForm, managers]);
  const isStep3Valid = true; // Sources CA optionnelles mais warning affiche
  const hasCASource = caSources.length > 0;

  // ==================== EXERCICE CALCULATION ====================

  const parseFiscalYearEnd = () => {
    const parts = fiscalYearEnd.split('/');
    if (parts.length === 2) {
      return { day: parseInt(parts[0]), month: parseInt(parts[1]) };
    }
    return { day: 31, month: 12 };
  };

  const getExercicesForSource = (source) => {
    if (!source.startDate || !source.endDate) return [];
    const { day: fyDay, month: fyMonth } = parseFiscalYearEnd();
    const startDate = new Date(source.startDate + '-01');
    const endDate = new Date(source.endDate + '-28');
    const exercices = [];
    let currentYear = startDate.getFullYear();
    let exerciceEndDate = new Date(currentYear, fyMonth - 1, fyDay);
    if (startDate > exerciceEndDate) currentYear++;

    while (exercices.length < 20) {
      const prevEnd = new Date(currentYear - 1, fyMonth - 1, fyDay);
      const exerciceStart = new Date(prevEnd);
      exerciceStart.setDate(exerciceStart.getDate() + 1);
      const exerciceEnd = new Date(currentYear, fyMonth - 1, fyDay);

      if (exerciceEnd >= startDate && exerciceStart <= endDate) {
        exercices.push({
          label: `Exercice ${currentYear}`,
          year: currentYear,
          startDate: exerciceStart.toLocaleDateString('fr-FR'),
          endDate: exerciceEnd.toLocaleDateString('fr-FR'),
          key: `${currentYear}`
        });
      }
      if (exerciceStart > endDate) break;
      currentYear++;
    }
    return exercices;
  };

  // Générer les exercices à partir des dates de prévision
  const forecastExercices = useMemo(() => {
    if (!forecastDates.startDate || !forecastDates.endDate) return [];
    const { day: fyDay, month: fyMonth } = parseFiscalYearEnd();
    // Les dates sont maintenant au format YYYY-MM-DD
    const startDate = new Date(forecastDates.startDate);
    const endDate = new Date(forecastDates.endDate);
    const creationDate = entityData.creationDate ? new Date(entityData.creationDate) : null;
    const exercices = [];

    // Determine first exercice boundaries based on creation date and firstExerciceType
    let firstExerciceStart = creationDate || startDate;
    let firstExerciceEndYear = firstExerciceStart.getFullYear();

    // Calculate the next fiscal year end after creation date
    let nextFYEnd = new Date(firstExerciceEndYear, fyMonth - 1, fyDay);
    if (nextFYEnd <= firstExerciceStart) {
      nextFYEnd = new Date(firstExerciceEndYear + 1, fyMonth - 1, fyDay);
    }

    // Check if creation date is aligned with fiscal year start
    // Fiscal year START is the day after fiscal year END
    const fyStartDay = fyDay === 31 && fyMonth === 12 ? 1 : fyDay + 1;
    const fyStartMonth = fyDay === 31 && fyMonth === 12 ? 1 : (fyDay >= 28 ? fyMonth + 1 : fyMonth);
    const isAlignedWithFiscalYear =
      firstExerciceStart.getDate() === fyStartDay &&
      (firstExerciceStart.getMonth() + 1) === fyStartMonth;

    // Apply short/long logic ONLY if NOT aligned with fiscal year
    // When aligned (e.g., Jan 1 creation with Dec 31 fiscal year end), always use normal 12-month exercices
    const firstExerciceType = entityData.firstExerciceType || 'long';
    let firstExerciceEnd;
    if (isAlignedWithFiscalYear) {
      firstExerciceEnd = nextFYEnd;
    } else {
      firstExerciceEnd = firstExerciceType === 'long'
        ? new Date(nextFYEnd.getFullYear() + 1, fyMonth - 1, fyDay)
        : nextFYEnd;
    }

    // Build exercices array
    let exerciceNumber = 1;
    let currentExerciceStart = firstExerciceStart;
    let currentExerciceEnd = firstExerciceEnd;

    while (exercices.length < 10) {
      // Check if exercice overlaps with forecast period
      if (currentExerciceEnd >= startDate && currentExerciceStart <= endDate) {
        exercices.push({
          label: `Exercice ${currentExerciceEnd.getFullYear()}`,
          year: currentExerciceEnd.getFullYear(),
          startDate: currentExerciceStart.toLocaleDateString('fr-FR'),
          endDate: currentExerciceEnd.toLocaleDateString('fr-FR'),
          key: `${currentExerciceEnd.getFullYear()}`,
          exerciceNumber,
          isFirstYear: exerciceNumber === 1,
          isSecondYear: exerciceNumber === 2
        });
      }

      // Move to next exercice (always 12 months after first)
      if (currentExerciceStart > endDate) break;

      exerciceNumber++;
      currentExerciceStart = new Date(currentExerciceEnd);
      currentExerciceStart.setDate(currentExerciceStart.getDate() + 1);
      currentExerciceEnd = new Date(currentExerciceEnd.getFullYear() + 1, fyMonth - 1, fyDay);
    }
    return exercices;
  }, [forecastDates.startDate, forecastDates.endDate, fiscalYearEnd, entityData.creationDate, entityData.firstExerciceType]);

  // ========== GESTION DES DONNÉES PAR EXERCICE ==========

  // Initialiser/obtenir les données d'un exercice
  const getExerciceDataForYear = useCallback((exerciceKey) => {
    return exerciceData[exerciceKey] || {
      // Compte 70 - Chiffre d'affaires
      caPrincipal: { label: '', amount: 0, chargesVariables: { type: 'fixed', value: 0 } },
      caSecondaires: [], // { id, label, amount, chargesVariables: { type: 'fixed', value } }
      // Compte 74 - Subventions d'exploitation
      subventions: [], // { id, label, amount, organisme }
      // Compte 75 - Autres produits de gestion
      autresProduits: [], // { id, label, amount }
      // Compte 76 - Produits financiers
      produitsFinanciers: { amount: 0 },
      // Dividendes - overrides par exercice (null = utiliser défaut global)
      dividendPercentOverride: null, // null = use global dividendPercent
      dividendPaymentDateOverride: null // null = use calculated date from delay
    };
  }, [exerciceData]);

  // Mettre à jour les données d'un exercice
  const updateExerciceData = useCallback((exerciceKey, updates) => {
    setExerciceData(prev => ({
      ...prev,
      [exerciceKey]: {
        ...getExerciceDataForYear(exerciceKey),
        ...updates
      }
    }));
  }, [getExerciceDataForYear]);

  // Mettre à jour le CA principal d'un exercice
  const updateCAPrincipal = useCallback((exerciceKey, updates) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      caPrincipal: { ...current.caPrincipal, ...updates }
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  // Ajouter une source de CA secondaire
  const addCASecondaire = useCallback((exerciceKey, label = '') => {
    const current = getExerciceDataForYear(exerciceKey);
    const newSecondaire = {
      id: Date.now(),
      label: label || `Activité secondaire ${current.caSecondaires.length + 1}`,
      amount: 0,
      chargesVariables: { type: 'percent', value: 0 }
    };
    updateExerciceData(exerciceKey, {
      caSecondaires: [...current.caSecondaires, newSecondaire]
    });
    return newSecondaire.id;
  }, [getExerciceDataForYear, updateExerciceData]);

  // Mettre à jour une source de CA secondaire
  const updateCASecondaire = useCallback((exerciceKey, sourceId, updates) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      caSecondaires: current.caSecondaires.map(s =>
        s.id === sourceId ? { ...s, ...updates } : s
      )
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  // Supprimer une source de CA secondaire
  const removeCASecondaire = useCallback((exerciceKey, sourceId) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      caSecondaires: current.caSecondaires.filter(s => s.id !== sourceId)
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  // ========== GESTION SUBVENTIONS (74) ==========
  const addSubvention = useCallback((exerciceKey) => {
    const current = getExerciceDataForYear(exerciceKey);
    const newSubvention = {
      id: Date.now(),
      label: '',
      amount: 0,
      organisme: ''
    };
    updateExerciceData(exerciceKey, {
      subventions: [...current.subventions, newSubvention]
    });
    return newSubvention.id;
  }, [getExerciceDataForYear, updateExerciceData]);

  const updateSubvention = useCallback((exerciceKey, subventionId, updates) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      subventions: current.subventions.map(s =>
        s.id === subventionId ? { ...s, ...updates } : s
      )
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  const removeSubvention = useCallback((exerciceKey, subventionId) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      subventions: current.subventions.filter(s => s.id !== subventionId)
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  // ========== GESTION AUTRES PRODUITS (75) ==========
  const addAutreProduit = useCallback((exerciceKey) => {
    const current = getExerciceDataForYear(exerciceKey);
    const newProduit = {
      id: Date.now(),
      label: '',
      amount: 0
    };
    updateExerciceData(exerciceKey, {
      autresProduits: [...current.autresProduits, newProduit]
    });
    return newProduit.id;
  }, [getExerciceDataForYear, updateExerciceData]);

  const updateAutreProduit = useCallback((exerciceKey, produitId, updates) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      autresProduits: current.autresProduits.map(p =>
        p.id === produitId ? { ...p, ...updates } : p
      )
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  const removeAutreProduit = useCallback((exerciceKey, produitId) => {
    const current = getExerciceDataForYear(exerciceKey);
    updateExerciceData(exerciceKey, {
      autresProduits: current.autresProduits.filter(p => p.id !== produitId)
    });
  }, [getExerciceDataForYear, updateExerciceData]);

  // ========== GESTION PRODUITS FINANCIERS (76) ==========
  const updateProduitsFinanciers = useCallback((exerciceKey, amount) => {
    updateExerciceData(exerciceKey, {
      produitsFinanciers: { amount: amount || 0 }
    });
  }, [updateExerciceData]);

  // ========== GESTION DIVIDENDES ==========
  // Calculer les infos dividendes pour un exercice
  const getDividendInfoForExercice = useCallback((exercice, resultatNet) => {
    const data = getExerciceDataForYear(exercice.key);

    // Utiliser override ou défaut global
    const effectivePercent = data.dividendPercentOverride !== null
      ? data.dividendPercentOverride
      : dividendPercent;

    // Calcul du montant
    const dividendAmount = resultatNet > 0 ? Math.round(resultatNet * effectivePercent / 100) : 0;

    // Calcul de la date de versement
    let paymentDate = null;
    let paymentDateFormatted = '';

    if (data.dividendPaymentDateOverride) {
      // Override date spécifique
      paymentDate = new Date(data.dividendPaymentDateOverride);
      paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } else {
      // Calculer depuis clôture + délai
      const [day, month] = (exercice.endDate || '31/12').split('/').map(Number);
      const year = parseInt(exercice.key);
      const endDate = new Date(year, month - 1, day);
      paymentDate = new Date(endDate);
      paymentDate.setMonth(paymentDate.getMonth() + dividendPaymentDelay);
      paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    }

    const taxYear = paymentDate ? paymentDate.getFullYear() : null;

    return {
      percent: effectivePercent,
      isOverridden: data.dividendPercentOverride !== null || data.dividendPaymentDateOverride !== null,
      amount: dividendAmount,
      paymentDate,
      paymentDateFormatted,
      paymentDateOverride: data.dividendPaymentDateOverride,
      taxYear
    };
  }, [getExerciceDataForYear, dividendPercent, dividendPaymentDelay]);

  // Mettre à jour le % dividendes pour un exercice
  const updateDividendPercent = useCallback((exerciceKey, percent) => {
    updateExerciceData(exerciceKey, {
      dividendPercentOverride: percent === dividendPercent ? null : percent
    });
  }, [updateExerciceData, dividendPercent]);

  // Mettre à jour la date de versement pour un exercice
  const updateDividendPaymentDate = useCallback((exerciceKey, date) => {
    updateExerciceData(exerciceKey, {
      dividendPaymentDateOverride: date || null
    });
  }, [updateExerciceData]);

  // Mettre à jour la date d'AG pour un exercice
  const updateAGDate = useCallback((exerciceKey, date) => {
    updateExerciceData(exerciceKey, {
      agDateOverride: date || null
    });
  }, [updateExerciceData]);

  // ========== AFFECTATION DU RÉSULTAT ==========
  // Calculate affectation du résultat for an exercice (réserve légale, distribuable, dividendes, report)
  // reportNMoins1 is the report à nouveau from previous exercice
  const getAffectationForExercice = useCallback((exercice, resultatNet, reportNMoins1 = 0) => {
    const data = getExerciceDataForYear(exercice.key);
    const capitalSocial = parseFloat(entityData.shareCapital) || 10000; // Default 10k€

    // Utiliser override ou défaut global pour le pourcentage de distribution
    const effectivePercent = data.dividendPercentOverride !== null && data.dividendPercentOverride !== undefined
      ? data.dividendPercentOverride
      : dividendPercent;

    // Réserve légale: 5% du résultat net, plafonné à 10% du capital
    const reserveLegalePlafond = capitalSocial * 0.10;
    const reserveLegaleCalculee = resultatNet > 0 ? Math.round(resultatNet * 0.05) : 0;
    const reserveLegale = Math.min(reserveLegaleCalculee, reserveLegalePlafond);

    // Distribuable = Résultat Net - Réserve légale + Report N-1
    const distribuable = resultatNet - reserveLegale + reportNMoins1;

    // Dividendes = Distribuable × % de distribution (si distribuable > 0)
    const dividendes = distribuable > 0 ? Math.round(distribuable * effectivePercent / 100) : 0;

    // Report à nouveau = Distribuable - Dividendes
    const reportNouveau = distribuable - dividendes;

    // Date AG par défaut: 6 mois après clôture
    let agDate = null;
    let agDateFormatted = '';
    if (data.agDateOverride) {
      agDate = new Date(data.agDateOverride);
      agDateFormatted = agDate.toLocaleDateString('fr-FR');
    } else {
      const [day, month] = (exercice.endDate || '31/12').split('/').map(Number);
      const year = parseInt(exercice.key);
      const endDate = new Date(year, month - 1, day);
      agDate = new Date(endDate);
      agDate.setMonth(agDate.getMonth() + 6);
      agDateFormatted = agDate.toLocaleDateString('fr-FR');
    }

    // Date de versement
    let paymentDate = null;
    let paymentDateFormatted = '';
    if (data.dividendPaymentDateOverride) {
      paymentDate = new Date(data.dividendPaymentDateOverride);
      paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR');
    } else if (agDate) {
      // Par défaut: AG + 15 jours
      paymentDate = new Date(agDate);
      paymentDate.setDate(paymentDate.getDate() + 15);
      paymentDateFormatted = paymentDate.toLocaleDateString('fr-FR');
    }

    const taxYear = paymentDate ? paymentDate.getFullYear() : null;

    return {
      capitalSocial,
      resultatNet,
      reserveLegale,
      reserveLegalePlafond,
      reportNMoins1,
      distribuable,
      percent: effectivePercent,
      dividendes,
      reportNouveau,
      agDate,
      agDateFormatted,
      agDateOverride: data.agDateOverride,
      paymentDate,
      paymentDateFormatted,
      paymentDateOverride: data.dividendPaymentDateOverride,
      taxYear,
      isOverridden: data.dividendPercentOverride !== null || data.agDateOverride !== null || data.dividendPaymentDateOverride !== null
    };
  }, [getExerciceDataForYear, entityData.shareCapital, dividendPercent]);

  // Calculer les charges variables pour un montant de CA
  const calculateChargesVariables = useCallback((ca, chargesVariables) => {
    if (!chargesVariables || ca === 0) return 0;
    if (chargesVariables.type === 'percent') {
      return Math.round(ca * (chargesVariables.value || 0) / 100);
    }
    return chargesVariables.value || 0;
  }, []);

  // CA Source management
  const addCASource = (sourceType) => {
    // Calculer date de fin par défaut (année courante + 2 ans, décembre)
    const defaultEndDate = `${new Date().getFullYear() + 2}-12`;

    // Convertir creationDate (YYYY-MM-DD) en format mois (YYYY-MM) pour les sources de CA
    const creationMonth = entityData.creationDate
      ? entityData.creationDate.substring(0, 7) // "2025-01-15" → "2025-01"
      : '';

    const newSource = {
      id: Date.now(),
      sourceType,
      label: sourceType === 'CA_PRINCIPAL' ? 'Activité principale' : '',
      // Si CA_PRINCIPAL et date création définie, pré-remplir avec le mois de création
      startDate: sourceType === 'CA_PRINCIPAL' && creationMonth
        ? creationMonth
        : '',
      endDate: sourceType === 'CA_PRINCIPAL' ? defaultEndDate : '',
      yearlyData: {} // L'utilisateur doit saisir les montants manuellement
    };

    setCaSources(prev => [...prev, newSource]);
    setExpandedSourceId(newSource.id);
  };

  const updateCASource = (index, updates) => {
    setCaSources(prev => prev.map((src, i) => i === index ? { ...src, ...updates } : src));
  };

  const removeCASource = (index) => {
    setCaSources(prev => prev.filter((_, i) => i !== index));
  };

  const updateYearlyData = (sourceIndex, key, data) => {
    setCaSources(prev => prev.map((src, i) => {
      if (i !== sourceIndex) return src;
      return {
        ...src,
        yearlyData: { ...src.yearlyData, [key]: { ...src.yearlyData?.[key], ...data } }
      };
    }));
  };

  // All exercices
  const coveredExercices = useMemo(() => {
    const map = new Map();
    caSources.forEach(source => {
      getExercicesForSource(source).forEach(ex => {
        if (!map.has(ex.key)) map.set(ex.key, ex);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.year - b.year);
  }, [caSources, fiscalYearEnd]);

  // Calculate exercice totals - avec prise en compte de l'année, type société et toutes hypothèses
  const calculateExerciceTotals = useCallback((exerciceKey, exerciceYear) => {
    // ========== 1. CHIFFRE D'AFFAIRES ==========
    let totalCA = 0;
    caSources.forEach(source => {
      const yearData = source.yearlyData?.[exerciceKey] || {};
      if (['CA_PRINCIPAL', 'CA_SECONDAIRE'].includes(source.sourceType)) {
        totalCA += yearData.amount || 0;
      }
    });

    // ========== 2. DÉTERMINER L'ANNÉE DE L'EXERCICE ==========
    const creationYear = entityData.creationDate
      ? new Date(entityData.creationDate).getFullYear()
      : null;
    const isFirstYear = creationYear && exerciceYear === creationYear;
    const isSecondYear = creationYear && exerciceYear === creationYear + 1;
    const exerciceNumber = creationYear ? exerciceYear - creationYear + 1 : 1;

    // ========== 3. CHARGES D'EXPLOITATION ==========
    let chargesExploitation = 0;
    if (totalExploitationCharges.fixed) chargesExploitation += totalExploitationCharges.fixed;
    if (totalExploitationCharges.percent) chargesExploitation += totalCA * totalExploitationCharges.percent / 100;

    // CFE : exonéré année 1, -50% année 2, plein tarif ensuite
    // CFE = max(base imposable × taux, minimum selon CA)
    const cfeCalculee = Math.round(cfeBaseImposable * fiscalParams.cfeTaux / 100);
    let cfeMin = 0;
    if (totalCA > 500000) cfeMin = 535;
    else if (totalCA > 250000) cfeMin = 380;
    else if (totalCA > 100000) cfeMin = 237;
    else cfeMin = 237; // Minimum par défaut

    const cfeAvantExo = Math.max(cfeCalculee, cfeMin);
    let cfe = cfeAvantExo;
    if (isFirstYear) {
      cfe = 0; // Exonéré année 1
    } else if (isSecondYear) {
      cfe = Math.round(cfeAvantExo * 0.5); // -50% année 2
    }
    chargesExploitation += cfe;

    // ========== 4. COÛT DIRIGEANTS (MULTI-MANAGERS) ==========
    let coutDirigeants = 0;
    const coutDirigeantsDetail = [];
    let acreAppliedToAny = false;

    managersWithStatus.forEach(manager => {
      const annualNet = manager.netMonthly * manager.months;
      if (annualNet === 0) return;

      // Calcul des charges sociales selon le statut (TNS vs Assimilé salarié)
      const baseCalc = calculateManagerSocialCharges(annualNet, manager.status);
      let charges = baseCalc.charges;
      let cout = baseCalc.cout;

      // ACRE : -50% charges sociales année 1 uniquement
      if (hasACRE && isFirstYear) {
        charges = charges * 0.5;
        cout = annualNet + charges;
        acreAppliedToAny = true;
      }

      // JEI : exonération charges sociales dirigeant (partielle, 7 ans max)
      if (advancedOptions.jei && exerciceNumber <= 7) {
        const jeiReduction = exerciceNumber <= 3 ? 0.5 : (exerciceNumber <= 5 ? 0.3 : 0.2);
        charges = charges * (1 - jeiReduction);
        cout = annualNet + charges;
      }

      coutDirigeantsDetail.push({
        role: manager.role,
        status: manager.status,
        net: annualNet,
        charges: Math.round(charges),
        cout: Math.round(cout),
        acreApplied: hasACRE && isFirstYear,
      });

      coutDirigeants += cout;
    });
    coutDirigeants = Math.round(coutDirigeants);

    // ========== 5. COÛT EMPLOYÉS ==========
    let coutEmployes = 0;

    if (hasEmployees) {
      if (chargeMode === 'detailed' && employeeProfiles.length > 0) {
        // Mode détaillé : calculer pour chaque profil avec prorata jour précis
        employeeProfiles.forEach(profile => {
          // Définir les bornes de l'exercice fiscal
          const exerciceStart = new Date(exerciceYear, 0, 1); // 1er janvier
          const exerciceEnd = new Date(exerciceYear, 11, 31); // 31 décembre

          // Dates du profil employé
          const profileStart = profile.startDate ? new Date(profile.startDate) : exerciceStart;
          const profileEnd = profile.isOngoing ? exerciceEnd :
            (profile.endDate ? new Date(profile.endDate) : exerciceEnd);

          // Si le profil ne chevauche pas l'exercice, 0 mois
          if (profileEnd < exerciceStart || profileStart > exerciceEnd) {
            return; // coutEmployes += 0
          }

          // Calcul de la période travaillée dans l'exercice
          const periodStart = profileStart > exerciceStart ? profileStart : exerciceStart;
          const periodEnd = profileEnd < exerciceEnd ? profileEnd : exerciceEnd;

          // Prorata en jours puis conversion en mois (30.42 jours/mois)
          const diffDays = Math.max(0, (periodEnd - periodStart) / (1000 * 60 * 60 * 24) + 1);
          const monthsWorked = Math.min(12, diffDays / 30.42);

          const grossAnnual = profile.grossMonthlySalary * monthsWorked * profile.headcount;
          let chargesPatronales = grossAnnual * 0.45; // ~45% charges patronales

          // JEI : exonération charges patronales salariés R&D
          if (advancedOptions.jei && exerciceNumber <= 7) {
            const jeiReduction = exerciceNumber <= 3 ? 0.5 : (exerciceNumber <= 5 ? 0.3 : 0.2);
            chargesPatronales = chargesPatronales * (1 - jeiReduction);
          }

          coutEmployes += grossAnnual + chargesPatronales;
        });
      } else {
        // Mode simplifié
        const masseSalarialeBrute = employeesData.count * employeesData.avgGrossSalary * employeesData.months;
        let chargesPatronales = masseSalarialeBrute * 0.45;

        // JEI : exonération
        if (advancedOptions.jei && exerciceNumber <= 7) {
          const jeiReduction = exerciceNumber <= 3 ? 0.5 : (exerciceNumber <= 5 ? 0.3 : 0.2);
          chargesPatronales = chargesPatronales * (1 - jeiReduction);
        }

        coutEmployes = masseSalarialeBrute + chargesPatronales;
      }
      coutEmployes = Math.round(coutEmployes);
    }

    // ========== 6. TOTAL CHARGES ==========
    const totalCharges = chargesExploitation + coutDirigeants + coutEmployes;
    const resultatBrut = totalCA - totalCharges;

    // ========== 7. IMPÔT SUR LES SOCIÉTÉS (IS) ==========
    let is = 0;
    let cirCredit = 0;

    if (entityData.taxRegime === 'IS' && resultatBrut > 0) {
      // JEI : exonération IS années 1-3, puis 50% années 4-5
      if (advancedOptions.jei) {
        if (exerciceNumber <= 3) {
          is = 0; // Exonération totale IS
        } else if (exerciceNumber <= 5) {
          // 50% exonération
          if (resultatBrut <= 42500) {
            is = resultatBrut * 0.15 * 0.5;
          } else {
            is = (42500 * 0.15 + (resultatBrut - 42500) * 0.25) * 0.5;
          }
        } else {
          // IS normal
          if (resultatBrut <= 42500) {
            is = resultatBrut * 0.15;
          } else {
            is = 42500 * 0.15 + (resultatBrut - 42500) * 0.25;
          }
        }
      } else {
        // IS normal (15% jusqu'à 42 500€, 25% au-delà)
        if (resultatBrut <= 42500) {
          is = resultatBrut * 0.15;
        } else {
          is = 42500 * 0.15 + (resultatBrut - 42500) * 0.25;
        }
      }

      // CIR : crédit d'impôt recherche (30% des dépenses R&D)
      if (advancedOptions.cir && advancedOptions.cirAmount > 0) {
        cirCredit = advancedOptions.cirAmount * 0.30;
        is = Math.max(0, is - cirCredit);
      }
    }

    // ========== 8. RÉSULTAT NET ==========
    const resultatNet = resultatBrut - is;
    const dividendes = resultatNet > 0 ? resultatNet * dividendPercent / 100 : 0;

    return {
      ca: totalCA,
      chargesExploitation,
      cfe,
      coutDirigeants, // Renommé de coutDirigeant
      coutDirigeant: coutDirigeants, // Compatibilité
      coutDirigeantsDetail,
      coutEmployes,
      totalCharges,
      resultatBrut,
      is: Math.round(is),
      cirCredit: Math.round(cirCredit),
      resultatNet: Math.round(resultatNet),
      dividendes: Math.round(dividendes),
      reserve: Math.round(resultatNet - dividendes),
      isFirstYear,
      isSecondYear,
      exerciceNumber,
      acreApplied: acreAppliedToAny,
      jeiApplied: advancedOptions.jei && exerciceNumber <= 7,
      managersCount: managersWithStatus.length,
    };
  }, [caSources, totalExploitationCharges, entityData.taxRegime, entityData.creationDate, advancedOptions, dividendPercent, hasEmployees, employeesData, employeeProfiles, fiscalParams, cfeBaseImposable, hasACRE, managersWithStatus, chargeMode]);

  // ==================== FORMAT HELPERS ====================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // ==================== EXERCICE DETAIL HELPERS ====================

  const calculateChargesForYear = (year, ca) => {
    if (chargeMode === 'simplified') {
      if (simplifiedCharges.usePercent) {
        return ca * (simplifiedCharges.totalPercent || 20) / 100;
      }
      return simplifiedCharges.totalFixed || 0;
    } else {
      // Mode détaillé : somme des charges activées
      let total = 0;
      Object.entries(detailedCharges).forEach(([key, data]) => {
        if (data.enabled) {
          if (data.type === 'percent') {
            total += ca * (data.value || 0) / 100;
          } else {
            total += data.value || 0;
          }
        }
      });
      return total;
    }
  };

  const openExerciceDetail = (exercice, source, sourceIndex) => {
    // Utiliser la nouvelle structure d'exercice si pas de source
    const data = getExerciceDataForYear(exercice.key);
    const totalCA = data.caPrincipal.amount +
      data.caSecondaires.reduce((sum, s) => sum + (s.amount || 0), 0);

    setSelectedExercice({
      ...exercice,
      source,
      sourceIndex,
      ca: source?.yearlyData?.[exercice.key]?.amount || totalCA
    });
    setShowDetailModal(true);
  };

  const handleExerciceDetailSave = (overrides) => {
    if (selectedExercice) {
      setExerciceOverrides(prev => ({
        ...prev,
        [selectedExercice.key]: overrides
      }));
    }
    setShowDetailModal(false);
    setSelectedExercice(null);
  };

  // Helper functions for first/second year detection
  const isFirstYearForExercice = (exerciceYear) => {
    if (!entityData.creationDate) return false;
    const creationYear = new Date(entityData.creationDate).getFullYear();
    return exerciceYear === creationYear;
  };

  const isSecondYearForExercice = (exerciceYear) => {
    if (!entityData.creationDate) return false;
    const creationYear = new Date(entityData.creationDate).getFullYear();
    return exerciceYear === creationYear + 1;
  };

  // ==================== NAVIGATION ====================

  const canGoNext = () => {
    switch (currentStep) {
      case 0: return isStep1Valid; // Step 1: nom obligatoire
      case 1: return isStep2Valid; // Step 2: charges optionnelles
      case 2: return isStep3Valid; // Step 3: sources CA (warning si vide mais permet de continuer)
      case 3: return true;         // Step 4: resume, pas de validation
      default: return true;
    }
  };

  // Warning message for step 3 if no CA source
  const getStepWarning = () => {
    if (currentStep === 2 && !hasCASource) {
      return "Aucune source de CA definie. Vous pouvez continuer mais le resume sera vide.";
    }
    return null;
  };

  const goNext = () => canGoNext() && currentStep < STEPS.length - 1 && setCurrentStep(currentStep + 1);
  const goBack = () => currentStep > 0 && setCurrentStep(currentStep - 1);

  // ==================== SAVE ====================

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // Enrich exerciceData with calculated values (dividendAmount, resultatNet, etc.)
      // Only keep exercice years within the forecast range - remove orphaned years
      const validKeys = new Set(forecastExercices.map(ex => ex.key));
      const enrichedExerciceData = {};
      Object.keys(exerciceData).forEach(key => {
        if (validKeys.has(key)) {
          enrichedExerciceData[key] = { ...exerciceData[key] };
        }
      });

      let runningReport = 0; // Running report à nouveau across exercices

      for (const exercice of forecastExercices) {
        const totals = calculateExerciceTotalsNew(exercice);
        const affectation = getAffectationForExercice(exercice, totals.resultatNet, runningReport);

        // Parse exercice end date for storage
        let exerciceEndDate = null;
        if (exercice.endDate) {
          const [day, month, year] = exercice.endDate.split('/').map(Number);
          exerciceEndDate = new Date(year, month - 1, day);
        }

        enrichedExerciceData[exercice.key] = {
          ...enrichedExerciceData[exercice.key],
          resultatNet: totals.resultatNet,
          isAmount: totals.isAmount,
          dividendAmount: affectation.dividendes,
          dividendPaymentDate: affectation.paymentDate ? affectation.paymentDate.toISOString() : null,
          dividendTaxYear: affectation.taxYear,
          reserveLegale: affectation.reserveLegale,
          reportNouveau: affectation.reportNouveau,
          exerciceEndDate: exerciceEndDate ? exerciceEndDate.toISOString() : null,
        };

        // Update running report for next exercice
        runningReport = affectation.reportNouveau;
      }

      const data = {
        name: entityData.name,
        legal_form: entityData.legalForm,
        tax_regime: entityData.taxRegime,
        activity_type: entityData.activityType,
        ape_code: entityData.apeCode,
        fiscal_year_end: fiscalYearEnd,
        first_exercice_type: entityData.firstExerciceType || 'long',
        share_capital: entityData.shareCapital,
        is_gerant_majoritaire: entityData.isGérantMajoritaire,
        creation_date: entityData.creationDate,
        postal_code: entityData.postalCode,
        // Dirigeants (multi-managers)
        managers: JSON.stringify(managersWithStatus.map(m => ({
          role: m.role,
          net_monthly: m.netMonthly,
          months: m.months,
          ownership_percent: m.ownershipPercent,
          status: m.status,
          entryDate: m.entryDate || entityData.creationDate || null,
          salaryStartDate: m.salaryStartDate || entityData.creationDate || null,
        }))),
        // Compatibilité avec ancien format
        manager_remuneration_net: primaryManager.netMonthly * primaryManager.months,
        manager_remuneration_months: primaryManager.months,
        manager_type: managerType,
        // Options
        has_acre: hasACRE,
        has_cga: hasCGA,
        tva_regime: tvaRegime,
        dividend_percent: dividendPercent,
        dividend_payment_delay: dividendPaymentDelay,
        // Charges
        charges_mode: chargeMode,
        charges: JSON.stringify(chargeMode === 'simplified' ? simplifiedCharges : detailedCharges),
        // Paramètres fiscaux (CFE, CVAE)
        fiscal_params: JSON.stringify(fiscalParams),
        advanced_options: JSON.stringify(advancedOptions),
        // Employés
        has_employees: hasEmployees,
        employees_count: hasEmployees ? employeesData.count : 0,
        employees_avg_salary: hasEmployees ? employeesData.avgGrossSalary : 0,
        employees_months: hasEmployees ? employeesData.months : 12,
        employee_profiles: chargeMode === 'detailed' ? JSON.stringify(employeeProfiles) : null,
        // Prévisions
        forecast_start_date: forecastDates.startDate || null,
        forecast_end_date: forecastDates.endDate || null,
        exercice_data: JSON.stringify(enrichedExerciceData),
        // Sources CA
        sources: caSources.map(s => ({
          source_type: s.sourceType,
          label: s.label,
          start_date: s.startDate,
          end_date: s.endDate,
          yearly_amounts: Object.entries(s.yearlyData || {}).map(([year, data]) => ({
            year: parseInt(year),
            amount: data.amount || 0,
          }))
        }))
      };
      await onSave(data);
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // ==================== RENDER STEP 1: IDENTITY ====================

  // Helper pour obtenir la classe CSS d'un champ
  // Pour les sections avec sélection (legalForm, taxRegime, etc.), on ne met field-filled
  // que sur les inputs simples, pas sur les sections à choix multiples
  const getFieldClass = (fieldName, isSelectionSection = false) => {
    if (activeField === fieldName) return 'field-active';
    // Pour les sections à sélection, pas de field-filled sur la section entière
    // car l'élément sélectionné a déjà sa propre classe .selected
    if (isSelectionSection) return '';
    if (isFieldFilled(fieldName)) return 'field-filled';
    return '';
  };

  const renderIdentityStep = () => (
    <div className="step-content identity-step">
      {/* Row 1: Nom + Forme juridique */}
      <div className="form-row-flex">
        <div className="form-section flex-2">
          <div className={`form-group ${getFieldClass('name')}`}>
            <h4>Dénomination sociale</h4>
            <input
              type="text"
              value={entityData.name}
              onChange={(e) => handleEntityChange('name', e.target.value)}
              placeholder="Ex: MakeMoney"
              className="input-name"
              autoFocus
            />
          </div>
          <div className={`form-group creation-date-group ${getFieldClass('creationDate')}`}>
            <label>Date de création</label>
            <input
              type="date"
              className="input-date"
              value={entityData.creationDate}
              onChange={(e) => handleEntityChange('creationDate', e.target.value)}
            />
            <span className="field-hint">Détermine le premier exercice</span>
          </div>
          <div className={`form-group ${getFieldClass('postalCode')}`}>
            <label>Code postal <Tooltip text="Détermine la commune et le taux de CFE applicable"><span className="help-icon">ⓘ</span></Tooltip></label>
            <div className="postal-with-city">
              <input
                type="text"
                value={entityData.postalCode}
                onChange={(e) => handleEntityChange('postalCode', e.target.value.replace(/\D/g, ''))}
                placeholder="75001"
                className="input-postal"
                maxLength={5}
              />
              {detectedCity && <span className="detected-city">→ {detectedCity}</span>}
            </div>
            <span className="field-hint">Pour calcul CFE</span>
          </div>
        </div>

        <div className="form-section flex-3">
          <h4 className={activeField === 'legalForm' ? 'title-active' : ''}>Forme juridique</h4>
          <div className="legal-form-grid">
            {LEGAL_FORMS.map(form => (
              <div
                key={form.value}
                className={`legal-form-card ${entityData.legalForm === form.value ? 'selected' : ''}`}
                onClick={() => handleEntityChange('legalForm', form.value)}
              >
                <div className="lf-header">
                  <span className="lf-label">{form.label}</span>
                  <span className={`lf-manager ${form.managerType === 'TNS' ? 'tns' : form.managerType === 'ASSIMILE_SALARIE' ? 'as' : 'none'}`}>
                    {form.managerType === 'TNS' ? 'TNS' : form.managerType === 'ASSIMILE_SALARIE' ? 'AS' : '—'}
                  </span>
                </div>
                <div className="lf-desc">{form.description}</div>
              </div>
            ))}
          </div>

          {/* SARL: Option gérant majoritaire */}
          {entityData.legalForm === 'SARL' && (
            <div className="gerant-option">
              <label>
                <input
                  type="checkbox"
                  checked={entityData.isGérantMajoritaire}
                  onChange={(e) => handleEntityChange('isGérantMajoritaire', e.target.checked)}
                />
                Gérant majoritaire (TNS)
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Row 2: Régime fiscal + Activité */}
      <div className="form-row-flex">
        <div className="form-section flex-1">
          <h4 className={activeField === 'taxRegime' ? 'title-active' : ''}>Régime fiscal</h4>
          <div className="regime-options">
            {TAX_REGIMES.map(regime => {
              const currentFormInfo = getLegalFormInfo(entityData.legalForm);
              const isRecommended = currentFormInfo && currentFormInfo.defaultTaxRegime === regime.value;
              return (
                <div
                  key={regime.value}
                  className={`regime-option ${entityData.taxRegime === regime.value ? 'selected' : ''} ${entityData.taxRegime === 'IS' && ['SCI', 'SNC'].includes(entityData.legalForm) ? 'locked' : ''}`}
                  onClick={() => handleTaxRegimeChange(regime.value)}
                >
                  <span className="regime-icon">{regime.icon}</span>
                  <span className="regime-label">
                    {regime.label}
                    {isRecommended && entityData.legalForm && (
                      <span className="recommended-badge">Recommandé</span>
                    )}
                  </span>
                  <span className="regime-desc">{regime.description}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="form-section flex-2">
          <h4 className={activeField === 'activityType' ? 'title-active' : ''}>Type d'activité</h4>
          <div className="activity-chips">
            {ACTIVITY_TYPES.map(type => (
              <button
                key={type.value}
                type="button"
                className={`chip ${entityData.activityType === type.value ? 'selected' : ''}`}
                onClick={() => handleEntityChange('activityType', type.value)}
                title={type.fullName}
              >
                <span className="chip-icon">{type.icon}</span>
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Détails */}
      <div className="form-row-flex">
        <div className={`form-group ${getFieldClass('shareCapital')}`}>
          <label>Capital social</label>
          <div className="input-suffix-wrapper">
            <input
              type="number"
              value={entityData.shareCapital}
              onChange={(e) => handleEntityChange('shareCapital', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
              min="1"
              placeholder="1000"
            />
            <span className="suffix">€</span>
          </div>
        </div>

        <div className="form-group">
          <label className={activeField === 'fiscalYearEnd' ? 'title-active' : ''}>Clôture exercice</label>
          <div className="fiscal-year-options">
            {['31/12', '30/06', '31/03', '30/09'].map(fy => (
              <button
                key={fy}
                type="button"
                className={`fy-btn ${entityData.fiscalYearEnd === fy ? 'selected' : ''}`}
                onClick={() => handleEntityChange('fiscalYearEnd', fy)}
              >
                {fy}
              </button>
            ))}
            <button
              type="button"
              className={`fy-btn ${entityData.fiscalYearEnd === 'custom' ? 'selected' : ''}`}
              onClick={() => handleEntityChange('fiscalYearEnd', 'custom')}
            >
              Autre
            </button>
          </div>
          {entityData.fiscalYearEnd === 'custom' && (
            <div className="fy-custom-wrapper">
              <input
                type="text"
                placeholder="JJ/MM (ex: 15/09)"
                value={entityData.fiscalYearEndCustom}
                onChange={(e) => {
                  // Formater automatiquement en JJ/MM
                  let val = e.target.value.replace(/[^0-9/]/g, '');
                  if (val.length === 2 && !val.includes('/')) {
                    val += '/';
                  }
                  if (val.length <= 5) {
                    handleEntityChange('fiscalYearEndCustom', val);
                  }
                }}
                maxLength={5}
                className="fy-custom-input"
              />
              <span className="fy-format-hint">Format: JJ/MM</span>
            </div>
          )}
          {/* Dividend settings - inline with fiscal year end */}
          <div className="dividend-settings-inline">
            <span className="dividend-inline-label">💰 Dividendes</span>
            <div className="dividend-inline-fields">
              <div className="dividend-inline-field">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={dividendPercent}
                  onChange={(e) => setDividendPercent(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                  className="dividend-pct-input"
                />
                <span className="field-suffix">% du résultat</span>
              </div>
              <div className="dividend-inline-field">
                <span className="field-prefix">versés</span>
                <input
                  type="number"
                  min="1"
                  max="9"
                  value={dividendPaymentDelay}
                  onChange={(e) => setDividendPaymentDelay(Math.min(9, Math.max(1, parseInt(e.target.value) || 6)))}
                  className="dividend-delay-input"
                />
                <span className="field-suffix">mois après clôture</span>
              </div>
            </div>
          </div>
        </div>

        {/* First Exercice Type - only show for non-31/12 fiscal year */}
        {entityData.fiscalYearEnd && entityData.fiscalYearEnd !== '31/12' && entityData.creationDate && (() => {
          // Calculate short and long exercice durations
          const fyEnd = entityData.fiscalYearEnd === 'custom' ? entityData.fiscalYearEndCustom : entityData.fiscalYearEnd;
          if (!fyEnd || fyEnd.length < 5) return null;

          const [fyDay, fyMonth] = fyEnd.split('/').map(Number);
          const creationDate = new Date(entityData.creationDate);
          const creationYear = creationDate.getFullYear();

          // Next fiscal year end after creation
          let shortEnd = new Date(creationYear, fyMonth - 1, fyDay);
          if (shortEnd <= creationDate) {
            shortEnd = new Date(creationYear + 1, fyMonth - 1, fyDay);
          }

          // Long exercice = one more year
          const longEnd = new Date(shortEnd.getFullYear() + 1, fyMonth - 1, fyDay);

          // Calculate months
          const shortMonths = Math.round((shortEnd - creationDate) / (1000 * 60 * 60 * 24 * 30.44));
          const longMonths = Math.round((longEnd - creationDate) / (1000 * 60 * 60 * 24 * 30.44));

          const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

          return (
            <div className="form-group first-exercice-group">
              <label>Type de premier exercice</label>
              <div className="fiscal-year-options">
                <button
                  type="button"
                  className={`fy-btn ${entityData.firstExerciceType === 'short' ? 'selected' : ''}`}
                  onClick={() => handleEntityChange('firstExerciceType', 'short')}
                >
                  Court (~{shortMonths} mois)
                </button>
                <button
                  type="button"
                  className={`fy-btn ${entityData.firstExerciceType === 'long' ? 'selected' : ''}`}
                  onClick={() => handleEntityChange('firstExerciceType', 'long')}
                >
                  Long (~{longMonths} mois)
                </button>
              </div>
              <span className="fy-format-hint">
                {entityData.firstExerciceType === 'short'
                  ? `${formatDate(creationDate)} → ${formatDate(shortEnd)}`
                  : `${formatDate(creationDate)} → ${formatDate(longEnd)}`
                }
              </span>
            </div>
          );
        })()}

        <div className="form-group ape-group">
          <label>Code APE</label>
          <div className="ape-search-wrapper">
            <input
              type="text"
              value={apeSearch || entityData.apeCode}
              onChange={(e) => {
                setApeSearch(e.target.value);
                setShowApeDropdown(true);
              }}
              onFocus={() => setShowApeDropdown(true)}
              placeholder="Rechercher..."
            />
            {showApeDropdown && filteredApeCodes.length > 0 && (
              <div className="ape-dropdown">
                {filteredApeCodes.slice(0, 10).map(ape => (
                  <div
                    key={ape.code}
                    className="ape-option"
                    onClick={() => {
                      handleEntityChange('apeCode', ape.code);
                      setApeSearch('');
                      setShowApeDropdown(false);
                    }}
                  >
                    <strong>{ape.code}</strong> - {ape.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {entityData.apeCode && (
            <span className="ape-selected">{entityData.apeCode}</span>
          )}
        </div>
      </div>
    </div>
  );

  // ==================== RENDER STEP 2: CHARGES ====================

  // Calcul masse salariale pour affichage
  const masseSalarialeBrute = useMemo(() => {
    if (!hasEmployees) return 0;
    return employeesData.count * employeesData.avgGrossSalary * employeesData.months;
  }, [hasEmployees, employeesData]);

  // Helper pour le label du rôle
  const getRoleLabel = (role) => {
    const labels = {
      'president': 'Président',
      'dg': 'Directeur Général',
      'gerant': 'Gérant',
      'co-gerant': 'Co-gérant',
    };
    return labels[role] || role;
  };

  const renderChargesStep = () => (
    <div className="step-content charges-step">
      {/* Résumé société */}
      <div className="entity-summary-bar">
        <span className="legal-badge">{entityData.legalForm || '—'}</span>
        <span className="entity-name">{entityData.name || 'Ma société'}</span>
        {entityData.taxRegime && (
          <span className={`tax-badge ${entityData.taxRegime.toLowerCase()}`}>{entityData.taxRegime}</span>
        )}
      </div>

      {/* ==================== SECTION DIRIGEANTS (toujours visible) ==================== */}
      <div className="form-section managers-section">
        <h4>
          <span className="section-icon">👤</span>
          <span className="pcg-badge">64</span>
          Rémunération dirigeant{managers.length > 1 ? 's' : ''}
          <Tooltip text="Saisissez le salaire BRUT mensuel. Les charges patronales (~42-45%) seront ajoutées pour calculer le coût total."><span className="help-icon">ⓘ</span></Tooltip>
        </h4>

        <div className={`charges-table personnel-table managers-grid ${entityData.legalForm === 'SARL' ? 'with-parts' : ''}`}>
          <div className="table-header">
            <div className="col-personnel-role">Fonction</div>
            <div className="col-personnel-status">Statut</div>
            <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire BRUT mensuel du dirigeant"><span className="help-icon">ⓘ</span></Tooltip></div>
            <div className="col-personnel-months">× Mois <Tooltip text="Nombre de mois de salaire par an (12 standard, 13 avec 13ème mois, 14...)"><span className="help-icon">ⓘ</span></Tooltip></div>
            <div className="col-personnel-salary-start">Début rémuné. <Tooltip text="Date de début de la rémunération (par défaut = date de création)"><span className="help-icon">ⓘ</span></Tooltip></div>
            {entityData.legalForm === 'SARL' && <div className="col-personnel-parts">% parts <Tooltip text="Pourcentage de parts sociales détenues"><span className="help-icon">ⓘ</span></Tooltip></div>}
            <div className="col-personnel-charges">Charges <Tooltip text="Charges patronales calculées automatiquement"><span className="help-icon">ⓘ</span></Tooltip></div>
            <div className="col-personnel-cout">Coût annuel <Tooltip text="Coût total = (Brut × Mois) + Charges"><span className="help-icon">ⓘ</span></Tooltip></div>
            <div className="col-personnel-actions"></div>
          </div>
          <div className="table-body">
            {managersWithStatus.map((manager, index) => {
              const annualBrut = manager.netMonthly * manager.months;
              const calcResult = calculateChargesFromBrut(annualBrut, manager.status);
              let chargesPatronales = calcResult.chargesPatronales;
              if (hasACRE) {
                chargesPatronales = Math.round(chargesPatronales * 0.5);
              }
              const coutTotal = annualBrut + chargesPatronales;

              return (
                <div key={manager.id} className="table-row personnel-row">
                  <div className="col-personnel-role">
                    <span className="role-name">{getRoleLabel(manager.role)}</span>
                  </div>
                  <div className="col-personnel-status">
                    <span
                      className={`status-tag ${manager.status === 'TNS' ? 'tns' : 'as'}`}
                      title={manager.status === 'TNS' ? 'Travailleur Non Salarié - Cotisations SSI ~45%' : 'Assimilé Salarié - Régime général ~54%'}
                    >
                      {calcResult.statusDisplay || (manager.status === 'ASSIMILE_SALARIE' ? 'AS' : manager.status)}
                    </span>
                  </div>
                  <div className="col-personnel-brut">
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        value={manager.netMonthly}
                        onChange={(e) => handleManagerChange(manager.id, 'netMonthly', parseInt(e.target.value) || 0)}
                        min="0"
                        step="100"
                        placeholder="0"
                      />
                      <span>€</span>
                    </div>
                  </div>
                  <div className="col-personnel-months">
                    <select
                      value={manager.months}
                      onChange={(e) => handleManagerChange(manager.id, 'months', parseInt(e.target.value) || 12)}
                      className="select-months"
                    >
                      <option value={10}>10</option>
                      <option value={11}>11</option>
                      <option value={12}>12</option>
                      <option value={13}>13</option>
                    </select>
                  </div>
                  <div className="col-personnel-salary-start">
                    <input
                      type="date"
                      value={manager.salaryStartDate || entityData.creationDate || ''}
                      onChange={(e) => handleManagerChange(manager.id, 'salaryStartDate', e.target.value)}
                      min={entityData.creationDate || ''}
                      className={`input-date ${isFirstMandatoryManager(manager) ? 'locked' : ''}`}
                      disabled={isFirstMandatoryManager(manager)}
                      title={isFirstMandatoryManager(manager) ? 'Date de début = date de création (premier dirigeant)' : ''}
                    />
                  </div>
                  {entityData.legalForm === 'SARL' && (
                    <div className="col-personnel-parts">
                      <div className="input-with-suffix">
                        <input
                          type="number"
                          value={manager.ownershipPercent || 0}
                          onChange={(e) => handleManagerChange(manager.id, 'ownershipPercent', parseInt(e.target.value) || 0)}
                          min="0"
                          max="100"
                          className="input-percent"
                        />
                        <span>%</span>
                      </div>
                    </div>
                  )}
                  <div className="col-personnel-charges">
                    {annualBrut === 0 ? (
                      <span className="non-remunere">—</span>
                    ) : (
                      <>
                        <span className="charges-value">
                          +{formatCurrency(chargesPatronales)}
                          {hasACRE && <span className="tag-reduction">ACRE</span>}
                        </span>
                        <span className="charges-rate">({Math.round(calcResult.tauxCharges * 100)}%)</span>
                      </>
                    )}
                  </div>
                  <div className="col-personnel-cout">
                    {annualBrut === 0 ? (
                      <span className="non-remunere">Non rémunéré</span>
                    ) : (
                      <strong>{formatCurrency(coutTotal)}</strong>
                    )}
                  </div>
                  <div className="col-personnel-actions">
                    {index > 0 && (
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeManager(manager.id)}
                        title="Supprimer"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {canAddManager && addManagerLabel && (
              <div className="table-row add-row">
                <div className="add-cell">
                  <button type="button" className="btn-add-manager" onClick={addManager}>
                    {addManagerLabel}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {entityData.legalForm === 'SARL' && (
          <div className="sarl-info">
            <span className="info-icon">ℹ️</span>
            <span>Gérance majoritaire si total parts gérants &gt; 50% → tous TNS</span>
          </div>
        )}
      </div>

      {/* ==================== SECTION OPTIONS (toutes les options réunies) ==================== */}
      <div className="form-section options-section">
        <h4>
          <span className="section-icon">⚙️</span>
          Options & Fiscalité
        </h4>

        <div className="options-grid">
          <label className="option-card">
            <input
              type="checkbox"
              checked={hasACRE}
              onChange={(e) => setHasACRE(e.target.checked)}
            />
            <div className="option-content">
              <span className="option-name">ACRE</span>
              <span className="option-desc">~50% charges sociales année 1</span>
            </div>
          </label>

          <label className="option-card">
            <input
              type="checkbox"
              checked={hasCGA}
              onChange={(e) => setHasCGA(e.target.checked)}
            />
            <div className="option-content">
              <span className="option-name">CGA/AGA</span>
              <span className="option-desc">Adhésion centre de gestion</span>
            </div>
          </label>

          <label className="option-card">
            <input
              type="checkbox"
              checked={advancedOptions.jei}
              onChange={(e) => setAdvancedOptions(prev => ({ ...prev, jei: e.target.checked }))}
            />
            <div className="option-content">
              <span className="option-name">JEI</span>
              <span className="option-desc">Jeune Entreprise Innovante</span>
            </div>
          </label>

          <label className="option-card">
            <input
              type="checkbox"
              checked={advancedOptions.cir}
              onChange={(e) => setAdvancedOptions(prev => ({ ...prev, cir: e.target.checked }))}
            />
            <div className="option-content">
              <span className="option-name">CIR</span>
              <span className="option-desc">Crédit Impôt Recherche</span>
            </div>
          </label>

          {advancedOptions.cir && (
            <div className="option-card cir-input">
              <div className="option-content">
                <span className="option-name">R&D</span>
                <div className="input-suffix-wrapper compact">
                  <input
                    type="number"
                    value={advancedOptions.cirAmount}
                    onChange={(e) => setAdvancedOptions(prev => ({ ...prev, cirAmount: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="1000"
                    placeholder="0"
                  />
                  <span className="suffix">€</span>
                </div>
              </div>
            </div>
          )}

          <div className="option-card tva-option">
            <div className="option-content">
              <span className="option-name">TVA</span>
              <select
                value={tvaRegime}
                onChange={(e) => setTvaRegime(e.target.value)}
                className="select-tva"
              >
                <option value="non_assujetti">Non assujetti</option>
                <option value="reel_simplifie">Réel simplifié</option>
                <option value="reel_normal">Réel normal</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SECTION CHARGES (avec toggle) ==================== */}
      <div className="form-section charges-section">
        <div className="section-header-with-toggle">
          <h4>
            <span className="section-icon">🏢</span>
            Charges
          </h4>
          <div className="mode-toggle">
            <button
              type="button"
              className={chargeMode === 'simplified' ? 'active' : ''}
              onClick={() => {
                if (chargeMode !== 'simplified') {
                  // Reset mode détaillé : vider les profils employés et les lignes de charges
                  setEmployeeProfiles([]);
                  setDetailedCharges(prev => {
                    const reset = {};
                    Object.keys(prev).forEach(k => {
                      reset[k] = { ...prev[k], value: prev[k].type === 'percent' ? 0 : 0 };
                    });
                    return reset;
                  });
                  setChargeMode('simplified');
                }
              }}
            >
              Simplifié
            </button>
            <button
              type="button"
              className={chargeMode === 'detailed' ? 'active' : ''}
              onClick={() => {
                if (chargeMode !== 'detailed') {
                  // Reset mode simplifié : remettre valeurs par défaut
                  setSimplifiedCharges({ totalPercent: null, totalFixed: 15000, usePercent: false });
                  setEmployeesData({ count: 1, avgGrossSalary: 2500, months: 12 });
                  setChargeMode('detailed');
                }
              }}
            >
              Détaillé
            </button>
          </div>
        </div>

        {chargeMode === 'simplified' ? (
          /* ==================== MODE SIMPLIFIÉ ==================== */
          <div className="charges-simplified">
            {/* Personnel simplifié - Compte 64 */}
            <div className="charge-subsection">
              <h5><span className="pcg-badge small">64</span> Personnel <Tooltip text="Saisissez le salaire BRUT. Charges patronales (~45%) ajoutées automatiquement."><span className="help-icon">ⓘ</span></Tooltip></h5>
              <div className="employees-switch">
                <label className="switch-option">
                  <input type="radio" checked={!hasEmployees} onChange={() => setHasEmployees(false)} />
                  <span>Sans employés</span>
                </label>
                <label className="switch-option">
                  <input type="radio" checked={hasEmployees} onChange={() => setHasEmployees(true)} />
                  <span>Avec employés</span>
                </label>
              </div>
              {hasEmployees && (
                <div className="simple-row employees-row">
                  <div className="form-group">
                    <label>Nombre</label>
                    <input
                      type="number"
                      value={employeesData.count}
                      onChange={(e) => setEmployeesData(prev => ({ ...prev, count: parseInt(e.target.value) || 1 }))}
                      min="1"
                      className="small-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Salaire brut moyen</label>
                    <div className="input-suffix-wrapper">
                      <input
                        type="number"
                        value={employeesData.avgGrossSalary}
                        onChange={(e) => setEmployeesData(prev => ({ ...prev, avgGrossSalary: parseInt(e.target.value) || 0 }))}
                        min="0"
                        step="100"
                      />
                      <span className="suffix">€/mois</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Mois/an</label>
                    <select
                      value={employeesData.months}
                      onChange={(e) => setEmployeesData(prev => ({ ...prev, months: parseInt(e.target.value) || 12 }))}
                    >
                      <option value={10}>10</option>
                      <option value={11}>11</option>
                      <option value={12}>12</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Exploitation simplifié - Compte 61-62 */}
            <div className="charge-subsection">
              <h5><span className="pcg-badge small">61-62</span> Services extérieurs</h5>
              <div className="charge-type-selector">
                <label className="charge-type-option">
                  <input
                    type="radio"
                    checked={!simplifiedCharges.usePercent}
                    onChange={() => setSimplifiedCharges(prev => ({ ...prev, usePercent: false }))}
                  />
                  <span className="option-label">Montant fixe</span>
                </label>
                <label className="charge-type-option">
                  <input
                    type="radio"
                    checked={simplifiedCharges.usePercent}
                    onChange={() => setSimplifiedCharges(prev => ({ ...prev, usePercent: true }))}
                  />
                  <span className="option-label">% du CA</span>
                </label>
              </div>
              <div className="charge-input-large">
                {simplifiedCharges.usePercent ? (
                  <div className="input-suffix-wrapper large">
                    <input
                      type="number"
                      value={simplifiedCharges.totalPercent ?? 20}
                      onChange={(e) => setSimplifiedCharges(prev => ({ ...prev, totalPercent: parseFloat(e.target.value) || 0 }))}
                      min="0"
                      max="100"
                    />
                    <span className="suffix">% du CA</span>
                  </div>
                ) : (
                  <div className="input-suffix-wrapper large">
                    <input
                      type="number"
                      value={simplifiedCharges.totalFixed ?? 0}
                      onChange={(e) => setSimplifiedCharges(prev => ({ ...prev, totalFixed: parseFloat(e.target.value) || 0 }))}
                      min="0"
                      step="1000"
                    />
                    <span className="suffix">€/an</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ==================== MODE DÉTAILLÉ ==================== */
          <div className="charges-detailed">
            {/* Personnel détaillé - Compte 64 */}
            <div className="charge-subsection">
              <h5><span className="pcg-badge small">64</span> Personnel <Tooltip text="Saisissez le salaire BRUT. Charges patronales (~45%) ajoutées automatiquement."><span className="help-icon">ⓘ</span></Tooltip></h5>
              <div className="employees-switch">
                <label className="switch-option">
                  <input type="radio" checked={!hasEmployees} onChange={() => setHasEmployees(false)} />
                  <span>Sans employés</span>
                </label>
                <label className="switch-option">
                  <input type="radio" checked={hasEmployees} onChange={() => setHasEmployees(true)} />
                  <span>Avec employés</span>
                </label>
              </div>

              {hasEmployees && (
                <div className="charges-table personnel-table employees-grid">
                  <div className="table-header">
                    <div className="col-emp-name">Profil <Tooltip text="Nom ou type de poste"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-emp-brut">Brut mensuel <Tooltip text="Salaire brut mensuel"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-emp-start">Début</div>
                    <div className="col-emp-end">Fin</div>
                    <div className="col-emp-count">Effectif <Tooltip text="Nombre de personnes à ce poste"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-emp-prorata">Prorata</div>
                    <div className="col-emp-charges">Charges <Tooltip text="Charges patronales ~45%"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-emp-cout">Coût annuel <Tooltip text="Coût total = Brut × Mois × Effectif × 1.45"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-emp-actions"></div>
                  </div>
                  <div className="table-body">
                    {employeeProfiles.map(profile => {
                      const months = calculateMonthsProrata(profile.startDate, profile.endDate, profile.isOngoing);
                      const annualBrut = profile.grossMonthlySalary * months * profile.headcount;
                      const chargesPatronales = Math.round(annualBrut * 0.45);
                      const coutTotal = annualBrut + chargesPatronales;

                      return (
                        <div key={profile.id} className="table-row personnel-row">
                          <div className="col-emp-name">
                            <input
                              type="text"
                              value={profile.profileName}
                              onChange={(e) => updateEmployeeProfile(profile.id, 'profileName', e.target.value)}
                              placeholder="Ex: Commercial"
                              className="input-name"
                            />
                          </div>
                          <div className="col-emp-brut">
                            <div className="input-with-suffix">
                              <input
                                type="number"
                                value={profile.grossMonthlySalary}
                                onChange={(e) => updateEmployeeProfile(profile.id, 'grossMonthlySalary', parseInt(e.target.value) || 0)}
                                min="0"
                              />
                              <span>€</span>
                            </div>
                          </div>
                          <div className="col-emp-start">
                            <input
                              type="date"
                              value={profile.startDate}
                              onChange={(e) => updateEmployeeProfile(profile.id, 'startDate', e.target.value)}
                              className="input-date"
                            />
                          </div>
                          <div className="col-emp-end">
                            {profile.isOngoing ? (
                              <label className="ongoing-label">
                                <input
                                  type="checkbox"
                                  checked={profile.isOngoing}
                                  onChange={(e) => updateEmployeeProfile(profile.id, 'isOngoing', e.target.checked)}
                                />
                                En cours
                              </label>
                            ) : (
                              <div className="end-date-cell">
                                <input
                                  type="date"
                                  value={profile.endDate}
                                  onChange={(e) => updateEmployeeProfile(profile.id, 'endDate', e.target.value)}
                                  className="input-date"
                                />
                                <button
                                  type="button"
                                  className="btn-ongoing"
                                  onClick={() => updateEmployeeProfile(profile.id, 'isOngoing', true)}
                                  title="Marquer comme en cours"
                                >
                                  ∞
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="col-emp-count">
                            <input
                              type="number"
                              value={profile.headcount}
                              onChange={(e) => updateEmployeeProfile(profile.id, 'headcount', parseInt(e.target.value) || 1)}
                              min="1"
                              className="count-input"
                            />
                          </div>
                          <div className="col-emp-prorata">
                            <span className="prorata-value">{months.toFixed(1)} mois</span>
                          </div>
                          <div className="col-emp-charges">
                            <span className="charges-value">+{formatCurrency(chargesPatronales)}</span>
                            <span className="charges-rate">(45%)</span>
                          </div>
                          <div className="col-emp-cout">
                            <strong>{formatCurrency(coutTotal)}</strong>
                          </div>
                          <div className="col-emp-actions">
                            <button
                              type="button"
                              className="btn-remove"
                              onClick={() => removeEmployeeProfile(profile.id)}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="table-row add-row">
                      <div className="add-cell">
                        <button type="button" className="btn-add-employee" onClick={addEmployeeProfile}>
                          + Ajouter un profil
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Exploitation détaillé - Compte 61-62 */}
            <div className="charge-subsection">
              <h5><span className="pcg-badge small">61-62</span> Services extérieurs</h5>
              <div className="detailed-charges">
                <div className="charges-list">
                  {COMMON_CHARGE_LINES.filter(line =>
                    !['salaires_employes', 'charges_sociales_employes'].includes(line.key)
                  ).map(line => {
                    const data = detailedCharges[line.key] || {};
                    return (
                      <div key={line.key} className={`charge-line ${data.enabled ? 'enabled' : ''}`}>
                        <label className="charge-toggle">
                          <input
                            type="checkbox"
                            checked={data.enabled || false}
                            onChange={() => handleDetailedChargeToggle(line.key)}
                          />
                          <span className="charge-icon">{line.icon}</span>
                          <span className="charge-label">{line.label}</span>
                        </label>

                        {data.enabled && (
                          <div className="charge-value-input">
                            <select
                              value={data.type}
                              onChange={(e) => handleDetailedChargeChange(line.key, 'type', e.target.value)}
                            >
                              <option value="fixed">€/an</option>
                              <option value="percent">% CA</option>
                            </select>
                            <input
                              type="number"
                              value={data.value || 0}
                              onChange={(e) => handleDetailedChargeChange(line.key, 'value', parseFloat(e.target.value) || 0)}
                              min="0"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ==================== RENDER STEP 3: REVENUS ====================

  // Fonction pour obtenir le numéro d'exercice depuis la création
  const getExerciceNumber = (exercice) => {
    if (!entityData.creationDate) return 1;
    const creationYear = new Date(entityData.creationDate).getFullYear();
    return exercice.year - creationYear + 1;
  };

  // Valeur par défaut CA estimé
  const defaultCAEstimate = 100000;

  // Hints dynamiques pour guider l'utilisateur
  const getStep3Hints = () => {
    const hints = [];
    if (caSources.length === 0) {
      hints.push({ type: 'action', message: 'Ajoutez une source de CA pour commencer' });
    } else {
      const hasEmptyCA = caSources.some(source => {
        const exercices = getExercicesForSource(source);
        return exercices.some(ex => !source.yearlyData?.[ex.key]?.amount);
      });
      if (hasEmptyCA) {
        hints.push({ type: 'warning', message: 'Certains exercices n\'ont pas de CA renseigné' });
      }
    }
    return hints;
  };

  const step3Hints = getStep3Hints();

  // Calculer les totaux pour un exercice (nouvelle structure)
  const calculateExerciceTotalsNew = useCallback((exercice) => {
    const data = getExerciceDataForYear(exercice.key);

    // Compte 70 - Chiffre d'affaires
    const caPrincipal = data.caPrincipal.amount || 0;
    const caSecondairesTotaux = data.caSecondaires.reduce((sum, s) => sum + (s.amount || 0), 0);
    const totalCA = caPrincipal + caSecondairesTotaux;

    // Compte 74 - Subventions
    const totalSubventions = data.subventions.reduce((sum, s) => sum + (s.amount || 0), 0);

    // Compte 75 - Autres produits
    const totalAutresProduits = data.autresProduits.reduce((sum, p) => sum + (p.amount || 0), 0);

    // Compte 76 - Produits financiers
    const totalProduitsFinanciers = data.produitsFinanciers?.amount || 0;

    // Total produits (Classe 7)
    const totalProduits = totalCA + totalSubventions + totalAutresProduits + totalProduitsFinanciers;

    // Charges variables du CA principal (Compte 60)
    const chargesVarPrincipal = calculateChargesVariables(caPrincipal, data.caPrincipal.chargesVariables);

    // Charges variables des CA secondaires
    const chargesVarSecondaires = data.caSecondaires.reduce((sum, s) =>
      sum + calculateChargesVariables(s.amount || 0, s.chargesVariables), 0);

    // Total charges variables (Compte 60)
    const totalChargesVar = chargesVarPrincipal + chargesVarSecondaires;

    // Dirigeants (Compte 64) - valeur calculée
    let coutDirigeantCalcule = 0;
    managersWithStatus.forEach(manager => {
      const annualNet = manager.netMonthly * manager.months;
      const baseCalc = calculateManagerSocialCharges(annualNet, manager.status);
      let charges = baseCalc.charges;

      // ACRE année 1
      if (hasACRE && exercice.isFirstYear) {
        charges = charges * 0.5;
      }
      // JEI
      if (advancedOptions.jei && exercice.exerciceNumber <= 7) {
        const jeiReduction = exercice.exerciceNumber <= 3 ? 0.5 : (exercice.exerciceNumber <= 5 ? 0.3 : 0.2);
        charges = charges * (1 - jeiReduction);
      }
      coutDirigeantCalcule += annualNet + charges;
    });
    const coutDirigeants = Math.round(coutDirigeantCalcule);

    // Employés (Compte 64) - calcul automatique
    let coutEmployes = 0;
    if (hasEmployees) {
      const masseSalarialeBrute = employeesData.count * employeesData.avgGrossSalary * employeesData.months;
      coutEmployes = Math.round(masseSalarialeBrute * 1.45);
    }

    // Charges exploitation - Compte 61-62 - calcul automatique
    let chargesExploitation = 0;
    if (totalExploitationCharges.fixed) chargesExploitation += totalExploitationCharges.fixed;
    if (totalExploitationCharges.percent) chargesExploitation += Math.round(totalCA * totalExploitationCharges.percent / 100);

    // CFE (Compte 63) - calcul automatique avec exonérations
    // CFE = MAX(Base × Taux, Cotisation minimum)
    // Base = valeur locative (avec local) OU base minimale (sans local)
    const baseMinSelonCA = fiscalParams.cfeBaseMinimale || getCfeBaseMinimaleFromCA(totalCA);
    const cotisationMin = Math.round(baseMinSelonCA * fiscalParams.cfeTaux / 100);

    let cfeBase = 0;
    let cfeCalculee = 0;
    if (fiscalParams.cfeHasLocal) {
      // Avec local: base = surface × tarif cadastral (approximation valeur locative)
      cfeBase = fiscalParams.cfeSurface * fiscalParams.cfeTarifM2;
      cfeCalculee = Math.round(cfeBase * fiscalParams.cfeTaux / 100);
      // Appliquer le minimum si la CFE calculée est trop basse
      cfeCalculee = Math.max(cfeCalculee, cotisationMin);
    } else {
      // Sans local: base minimale selon CA (barème national)
      cfeBase = baseMinSelonCA;
      cfeCalculee = cotisationMin;
    }
    const cfeAvantExo = cfeCalculee;
    let cfeAuto = cfeAvantExo;
    if (exercice.isFirstYear) cfeAuto = 0; // Exonération 100% année 1
    else if (exercice.isSecondYear) cfeAuto = Math.round(cfeAvantExo * 0.5); // -50% année 2
    // Utiliser l'override si défini, sinon la valeur calculée
    const cfe = exerciceOverrides[exercice.key]?.cfe ?? cfeAuto;

    // CVAE (Compte 63) - applicable si CA >= 500 000 €
    // Taux CVAE progressif selon CA (réforme 2021+)
    let cvaeAuto = 0;
    if (totalProduits >= 500000) {
      const valeurAjoutee = totalProduits * (fiscalParams.cvaeVAPercent / 100);
      // Taux progressif
      let tauxCVAE = 0;
      if (totalProduits >= 500000 && totalProduits < 3000000) tauxCVAE = 0.0025;
      else if (totalProduits >= 3000000 && totalProduits < 10000000) tauxCVAE = 0.0045;
      else if (totalProduits >= 10000000 && totalProduits < 50000000) tauxCVAE = 0.0055;
      else if (totalProduits >= 50000000) tauxCVAE = 0.0075;
      cvaeAuto = Math.round(valeurAjoutee * tauxCVAE);
    }
    // Utiliser l'override si défini, sinon la valeur calculée
    const cvae = exerciceOverrides[exercice.key]?.cvae ?? cvaeAuto;

    // Total compte 63 (Impôts et taxes)
    const totalImpotsEtTaxes = cfe + cvae;

    // Total charges (Classe 6)
    const chargesGlobales = coutDirigeants + coutEmployes + chargesExploitation + totalImpotsEtTaxes + totalChargesVar;

    // Résultat d'exploitation (avant IS)
    const resultatBrut = totalProduits - chargesGlobales;
    // Résultat fiscal pour calcul IS (= résultat brut, simplifié)
    const resultatAvantIS = resultatBrut;

    // IS (Impôt sur les Sociétés) - PME: 15% jusqu'à 42 500€, 25% au-delà
    let isAmount = 0;
    const seuilIS = 42500;
    if (resultatAvantIS > 0) {
      if (resultatAvantIS <= seuilIS) {
        isAmount = Math.round(resultatAvantIS * 0.15);
      } else {
        const isPart1 = Math.round(seuilIS * 0.15);
        const isPart2 = Math.round((resultatAvantIS - seuilIS) * 0.25);
        isAmount = isPart1 + isPart2;
      }
    }

    // Résultat net après IS
    const resultatNet = resultatAvantIS - isAmount;

    return {
      // Produits
      caPrincipal,
      caSecondairesTotaux,
      totalCA,
      totalSubventions,
      totalAutresProduits,
      totalProduitsFinanciers,
      totalProduits,
      // Charges - valeurs calculées automatiquement
      chargesVarPrincipal,
      chargesVarSecondaires,
      totalChargesVar,
      chargesGlobales,
      coutDirigeants,
      coutEmployes,
      chargesExploitation,
      cfe,
      cvae,
      totalImpotsEtTaxes,
      // Résultat
      resultatBrut,
      resultatAvantIS,
      isAmount,
      resultatNet
    };
  }, [getExerciceDataForYear, exerciceData, calculateChargesVariables, managersWithStatus, calculateManagerSocialCharges,
      hasACRE, advancedOptions.jei, hasEmployees, employeesData, totalExploitationCharges, fiscalParams, cfeBaseImposable, exerciceOverrides]);

  // Mode global Step 3 : hérite de chargeMode (Step 2) mais peut être modifié
  // null = hérite de Step 2, sinon 'simplified' ou 'detailed'
  const [step3Mode, setStep3Mode] = useState(null);

  // Mode effectif pour Step 3 : si override existe, l'utiliser, sinon hériter de Step 2
  const getStep3Mode = useCallback(() => step3Mode || chargeMode, [step3Mode, chargeMode]);

  // Toggle le mode global Step 3
  const toggleStep3Mode = () => {
    const current = getStep3Mode();
    setStep3Mode(current === 'simplified' ? 'detailed' : 'simplified');
  };

  const renderCAStep = () => {
    const hasEndDate = forecastDates.endDate;
    const isDetailedMode = getStep3Mode() === 'detailed';

    // Résumé des hypothèses Step 2
    const step2Summary = {
      dirigeants: managersWithStatus.length,
      coutDirigeantsAnnuel: managersWithStatus.reduce((sum, m) => {
        const baseCalc = calculateManagerSocialCharges(m.netMonthly * m.months, m.status);
        return sum + m.netMonthly * m.months + baseCalc.charges;
      }, 0),
      hasEmployees,
      coutEmployesAnnuel: hasEmployees ? Math.round(employeesData.count * employeesData.avgGrossSalary * employeesData.months * 1.45) : 0,
      chargesExploitation: totalExploitationCharges
    };

    return (
      <div className="step-content ca-step">
        {/* Résumé hypothèses Step 2 */}
        <div className="step2-summary">
          <span className="summary-label">Hypothèses :</span>
          <span className="summary-item">
            <strong>{step2Summary.dirigeants}</strong> dirigeant{step2Summary.dirigeants > 1 ? 's' : ''}
            <em>({formatCurrency(step2Summary.coutDirigeantsAnnuel)}/an)</em>
          </span>
          {step2Summary.hasEmployees && (
            <span className="summary-item">
              <strong>{employeesData.count}</strong> employé{employeesData.count > 1 ? 's' : ''}
              <em>({formatCurrency(step2Summary.coutEmployesAnnuel)}/an)</em>
            </span>
          )}
          <span className="summary-item">
            Exploitation: <em>{step2Summary.chargesExploitation.fixed ? formatCurrency(step2Summary.chargesExploitation.fixed) : `${step2Summary.chargesExploitation.percent}% CA`}</em>
          </span>
          {hasACRE && <span className="summary-tag">ACRE</span>}
          {advancedOptions.jei && <span className="summary-tag jei">JEI</span>}
        </div>

        {/* Header avec dates */}
        <div className={`ca-dates-header ${!hasEndDate ? 'needs-end-date' : ''}`}>
          <div className="date-field">
            <label>Début</label>
            <input
              type="date"
              value={forecastDates.startDate}
              onChange={(e) => setForecastDates(prev => ({ ...prev, startDate: e.target.value }))}
              className="source-date"
            />
          </div>
          <span className="date-sep">→</span>
          <div className={`date-field ${!hasEndDate ? 'highlight' : ''}`}>
            <label>Fin {!hasEndDate && <span className="required">*</span>}</label>
            <input
              type="date"
              value={forecastDates.endDate}
              onChange={(e) => setForecastDates(prev => ({ ...prev, endDate: e.target.value }))}
              className={`source-date ${!hasEndDate ? 'pulse' : ''}`}
              autoFocus={!hasEndDate}
            />
          </div>
          {hasEndDate && forecastExercices.length > 0 && (
            <div className="mode-selector">
              <span className="mode-selector-label">Mode :</span>
              <div className="mode-selector-cards">
                <Tooltip text="Vue résumée avec totaux agrégés par exercice. Idéal pour une vision rapide.">
                  <button
                    type="button"
                    className={`mode-card ${!isDetailedMode ? 'selected' : ''}`}
                    onClick={() => setStep3Mode('simplified')}
                  >
                    <span className="mode-card-icon">📊</span>
                    <span className="mode-card-label">Simplifié</span>
                  </button>
                </Tooltip>
                <Tooltip text="Vue complète avec le détail de chaque catégorie. Pour un contrôle fin des données.">
                  <button
                    type="button"
                    className={`mode-card ${isDetailedMode ? 'selected' : ''}`}
                    onClick={() => setStep3Mode('detailed')}
                  >
                    <span className="mode-card-icon">📋</span>
                    <span className="mode-card-label">Détaillé</span>
                  </button>
                </Tooltip>
              </div>
            </div>
          )}
        </div>

        {/* Message si pas de date de fin */}
        {!hasEndDate ? (
          <div className="empty-sources waiting">
            <span className="arrow-up">↑</span>
            <p>Saisissez la date de fin pour générer les exercices</p>
            <span className="empty-hint">Clôture fiscale : {fiscalYearEnd || '31/12'}</span>
          </div>
        ) : forecastExercices.length === 0 ? (
          <div className="empty-sources">
            <p>Aucun exercice dans cette période</p>
          </div>
        ) : (
          /* Liste des exercices - format compact */
          <div className="exercices-compact-list">
            {(() => {
              // Pre-calculate affectations with cascading report
              const affectationsMap = {};
              let runningReport = 0;
              forecastExercices.forEach((ex) => {
                const exTotals = calculateExerciceTotalsNew(ex);
                const aff = getAffectationForExercice(ex, exTotals.resultatNet, runningReport);
                affectationsMap[ex.key] = aff;
                runningReport = aff.reportNouveau;
              });

              return forecastExercices.map((exercice, index) => {
              const data = getExerciceDataForYear(exercice.key);
              const totals = calculateExerciceTotalsNew(exercice);
              const affectation = affectationsMap[exercice.key];

              return (
                <div key={exercice.key} className="exercice-card">
                  {/* Header exercice compact */}
                  <div className="exercice-header">
                    <strong>{exercice.label}</strong>
                    <span className="periode">{exercice.startDate} → {exercice.endDate}</span>
                    {exercice.isFirstYear && <span className="tag y1">An 1</span>}
                    {exercice.isSecondYear && <span className="tag y2">An 2</span>}
                  </div>

                  {/* Tableau compact */}
                  <div className="exercice-body">
                    {/* PRODUITS (Classe 7) */}
                    <div className="section-row produits">
                      <div className="section-label">
                        <span className="icon">+</span>
                        <span className="pcg-num">7</span>
                        Produits
                      </div>
                      <div className="section-value">{formatCurrency(totals.totalProduits)}</div>
                    </div>

                    {/* CA - éditable (70) */}
                    <div className="line-row ca">
                      <div className="line-label">
                        <span className="pcg-num">70</span>
                        {isDetailedMode ? (
                          <input
                            type="text"
                            value={data.caPrincipal.label || ''}
                            onChange={(e) => updateCAPrincipal(exercice.key, { label: e.target.value })}
                            placeholder="CA Principal"
                            className="label-input"
                          />
                        ) : (
                          <span>Chiffre d'affaires</span>
                        )}
                      </div>
                      <div className="line-input">
                        <input
                          type="number"
                          value={data.caPrincipal.amount || ''}
                          onChange={(e) => updateCAPrincipal(exercice.key, { amount: parseFloat(e.target.value) || 0 })}
                          placeholder="0"
                        />
                        <span>€</span>
                      </div>
                    </div>

                    {/* CA Secondaires en mode détaillé (70) */}
                    {isDetailedMode && data.caSecondaires.map((source) => (
                      <div key={source.id} className="line-row ca sub">
                        <div className="line-label">
                          <span className="pcg-num">70</span>
                          <input
                            type="text"
                            value={source.label || ''}
                            onChange={(e) => updateCASecondaire(exercice.key, source.id, { label: e.target.value })}
                            placeholder="Activité secondaire"
                            className="label-input"
                          />
                        </div>
                        <div className="line-input">
                          <input
                            type="number"
                            value={source.amount || ''}
                            onChange={(e) => updateCASecondaire(exercice.key, source.id, { amount: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                          />
                          <span>€</span>
                          <button type="button" className="mini-btn del" onClick={() => removeCASecondaire(exercice.key, source.id)}>×</button>
                        </div>
                      </div>
                    ))}

                    {/* Subventions en mode détaillé (74) */}
                    {isDetailedMode && data.subventions.map((sub) => (
                      <div key={sub.id} className="line-row other">
                        <div className="line-label">
                          <span className="pcg-num">74</span>
                          <input
                            type="text"
                            value={sub.label || ''}
                            onChange={(e) => updateSubvention(exercice.key, sub.id, { label: e.target.value })}
                            placeholder="Subvention"
                            className="label-input"
                          />
                        </div>
                        <div className="line-input">
                          <input
                            type="number"
                            value={sub.amount || ''}
                            onChange={(e) => updateSubvention(exercice.key, sub.id, { amount: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                          />
                          <span>€</span>
                          <button type="button" className="mini-btn del" onClick={() => removeSubvention(exercice.key, sub.id)}>×</button>
                        </div>
                      </div>
                    ))}

                    {/* Autres produits en mode détaillé (75) */}
                    {isDetailedMode && data.autresProduits.map((prod) => (
                      <div key={prod.id} className="line-row other">
                        <div className="line-label">
                          <span className="pcg-num">75</span>
                          <input
                            type="text"
                            value={prod.label || ''}
                            onChange={(e) => updateAutreProduit(exercice.key, prod.id, { label: e.target.value })}
                            placeholder="Autre produit"
                            className="label-input"
                          />
                        </div>
                        <div className="line-input">
                          <input
                            type="number"
                            value={prod.amount || ''}
                            onChange={(e) => updateAutreProduit(exercice.key, prod.id, { amount: parseFloat(e.target.value) || 0 })}
                            placeholder="0"
                          />
                          <span>€</span>
                          <button type="button" className="mini-btn del" onClick={() => removeAutreProduit(exercice.key, prod.id)}>×</button>
                        </div>
                      </div>
                    ))}

                    {/* Boutons ajout produits - en dessous des produits, AVANT les charges */}
                    {isDetailedMode && (
                      <div className="add-row products-add">
                        <button type="button" onClick={() => addCASecondaire(exercice.key)}>+ CA</button>
                        <button type="button" onClick={() => addSubvention(exercice.key)}>+ Subvention</button>
                        <button type="button" onClick={() => addAutreProduit(exercice.key)}>+ Autre</button>
                      </div>
                    )}

                    {/* CHARGES (Classe 6) */}
                    <div className={`section-row charges ${!isDetailedMode ? 'clickable' : ''}`}
                      onClick={!isDetailedMode ? () => setActiveModal({ type: 'chargeSummary', exerciceKey: exercice.key, exercice }) : undefined}
                    >
                      <div className="section-label">
                        <span className="icon">−</span>
                        <span className="pcg-num">6</span>
                        Charges
                        {!isDetailedMode && <span className="expand-hint">cliquer pour détail</span>}
                      </div>
                      <div className="section-value neg">-{formatCurrency(totals.chargesGlobales)}</div>
                    </div>

                    {/* Détail charges selon PCG - seulement en mode détaillé */}
                    {isDetailedMode && (
                      <>
                        {/* 60 - Achats (charges variables liées au CA) */}
                        <div className="line-row charge-line">
                          <div className="line-label">
                            <span className="pcg-num">60</span>
                            Achats
                          </div>
                          <div className="line-value-with-btn">
                            <span className="neg">-{formatCurrency(totals.totalChargesVar)}</span>
                            <button
                              type="button"
                              className="mini-btn info"
                              onClick={() => setActiveModal({ type: 'chargeAchats', exerciceKey: exercice.key, exercice })}
                              title="Configurer les charges variables par activité"
                            >⚙</button>
                          </div>
                        </div>

                        {/* 61-62 - Services extérieurs */}
                        <div className="line-row charge-line">
                          <div className="line-label">
                            <span className="pcg-num">61-62</span>
                            Services ext.
                          </div>
                          <div className="line-value-with-btn">
                            <span className="neg">-{formatCurrency(totals.chargesExploitation)}</span>
                            <button
                              type="button"
                              className="mini-btn info"
                              onClick={() => setActiveModal({ type: 'chargeExploitation', exerciceKey: exercice.key, exercice })}
                              title="Voir le détail des services extérieurs"
                            >ⓘ</button>
                          </div>
                        </div>

                        {/* 63 - Impôts, taxes et versements */}
                        <div className="line-row charge-line">
                          <div className="line-label">
                            <span className="pcg-num">63</span>
                            Impôts, taxes
                            {exercice.isFirstYear && <span className="mini-tag exo">CFE exo</span>}
                            {exercice.isSecondYear && <span className="mini-tag reduc">CFE -50%</span>}
                            {totals.cvae > 0 && <span className="mini-tag cvae">+CVAE</span>}
                          </div>
                          <div className="line-value-with-btn">
                            <span className="neg">-{formatCurrency(totals.totalImpotsEtTaxes)}</span>
                            <button
                              type="button"
                              className="mini-btn info"
                              onClick={() => setActiveModal({ type: 'chargeImpots', exerciceKey: exercice.key, exercice })}
                              title="Voir le détail CFE, CVAE, taxes"
                            >ⓘ</button>
                          </div>
                        </div>

                        {/* 64 - Charges de personnel */}
                        <div className="line-row charge-line">
                          <div className="line-label">
                            <span className="pcg-num">64</span>
                            Personnel
                          </div>
                          <div className="line-value-with-btn">
                            <span className="neg">-{formatCurrency(totals.coutDirigeants + totals.coutEmployes)}</span>
                            <button
                              type="button"
                              className="mini-btn info"
                              onClick={() => setActiveModal({ type: 'chargePersonnel', exerciceKey: exercice.key, exercice })}
                              title="Voir le détail dirigeants et employés"
                            >ⓘ</button>
                          </div>
                        </div>
                      </>
                    )}

                    {/* RÉSULTAT AVANT IS */}
                    <div className={`section-row resultat-avant-is ${totals.resultatAvantIS < 0 ? 'neg' : 'pos'}`}>
                      <div className="section-label">
                        <span className="icon">=</span>
                        Résultat avant IS
                      </div>
                      <div className="section-value">{formatCurrency(totals.resultatAvantIS)}</div>
                    </div>

                    {/* IS - Impôt sur les Sociétés */}
                    {totals.isAmount > 0 && (
                      <div className="section-row is-row">
                        <div className="section-label">
                          <span className="icon">−</span>
                          IS <span className="is-rate">(15%/25%)</span>
                        </div>
                        <div className="section-value neg">-{formatCurrency(totals.isAmount)}</div>
                      </div>
                    )}

                    {/* RÉSULTAT NET */}
                    <div className={`section-row resultat ${totals.resultatNet < 0 ? 'neg' : 'pos'}`}>
                      <div className="section-label">
                        <span className="icon">=</span>
                        <strong>Résultat Net</strong>
                      </div>
                      <div className="section-value"><strong>{formatCurrency(totals.resultatNet)}</strong></div>
                    </div>

                    {/* AFFECTATION DU RÉSULTAT */}
                    {totals.resultatNet !== 0 && (
                      <>
                        {/* Séparateur pointillé */}
                        <div className="affectation-separator"></div>

                        {/* Ligne Affectation avec configs */}
                        <div className={`section-row affectation-row ${affectation.isOverridden ? 'overridden' : ''}`}>
                          <div className="section-label affectation-label">
                            <span className="icon">📋</span>
                            <span className="affectation-text">Affectation</span>
                          </div>
                          <div className="section-value affectation-config">
                            <span className="config-group">
                              <span className="config-label">AG:</span>
                              <input
                                type="date"
                                value={affectation.agDateOverride || ''}
                                onChange={(e) => updateAGDate(exercice.key, e.target.value)}
                                className="affectation-date-input"
                                title={`AG prévue: ${affectation.agDateFormatted}`}
                              />
                              {!affectation.agDateOverride && (
                                <span className="config-default">{affectation.agDateFormatted}</span>
                              )}
                            </span>
                            <span className="config-group">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={affectation.percent}
                                onChange={(e) => updateDividendPercent(exercice.key, parseFloat(e.target.value) || 0)}
                                className="affectation-pct-input"
                                title="% du distribuable à distribuer"
                              />
                              <span className="config-suffix">%</span>
                            </span>
                            <span className="config-group">
                              <span className="config-label">Versé:</span>
                              <input
                                type="date"
                                value={affectation.paymentDateOverride || ''}
                                onChange={(e) => updateDividendPaymentDate(exercice.key, e.target.value)}
                                className="affectation-date-input"
                                title={`Versement prévu: ${affectation.paymentDateFormatted}`}
                              />
                              {!affectation.paymentDateOverride && (
                                <span className="config-default">{affectation.paymentDateFormatted}</span>
                              )}
                            </span>
                            <button
                              type="button"
                              className="affectation-detail-btn"
                              onClick={() => setActiveModal({
                                type: 'affectationDetail',
                                exercice,
                                affectation
                              })}
                              title="Voir le détail de l'affectation"
                            >ⓘ</button>
                          </div>
                        </div>

                        {/* Ligne Dividendes */}
                        <div className="section-row dividendes-result-row">
                          <div className="section-label">
                            <span className="icon">💰</span>
                            <span>Dividendes</span>
                            {affectation.taxYear && <span className="tax-year-badge">IR {affectation.taxYear}</span>}
                          </div>
                          <div className="section-value pos">{formatCurrency(affectation.dividendes)}</div>
                        </div>

                        {/* Ligne Report à nouveau */}
                        <div className="section-row report-nouveau-row">
                          <div className="section-label">
                            <span className="icon">➡️</span>
                            <span>Report à nouveau</span>
                            {index < forecastExercices.length - 1 && <span className="report-hint">→ exercice suivant</span>}
                          </div>
                          <div className="section-value">{formatCurrency(affectation.reportNouveau)}</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            });
            })()}
          </div>
        )}

        {/* Modal 64 Personnel - STYLE GRILLE */}
        {activeModal?.type === 'chargePersonnel' && (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">64</span> Charges de personnel</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Saisissez les salaires BRUT - les charges patronales sont calculées automatiquement
                </p>

                {/* Section Dirigeants - GRILLE */}
                <div className="personnel-section">
                  <div className="section-header">
                    <h5>Dirigeants</h5>
                  </div>

                  {managersWithStatus.length > 0 ? (
                    <div className="charges-table personnel-table">
                      <div className="table-header">
                        <div className="col-personnel-role">Fonction</div>
                        <div className="col-personnel-status">Statut</div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire BRUT mensuel du dirigeant"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">× Mois <Tooltip text="Nombre de mois de salaire par an (12 standard, 13 avec 13ème mois)"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges <Tooltip text="Charges patronales calculées automatiquement sur le brut"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût total <Tooltip text="Coût total pour l'entreprise = Brut × Mois + Charges"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-actions"></div>
                      </div>
                      <div className="table-body">
                        {managersWithStatus.map((manager, idx) => {
                          const annualBrut = manager.netMonthly * manager.months;
                          const calcResult = calculateChargesFromBrut(annualBrut, manager.status);
                          let chargesPatronales = calcResult.chargesPatronales;

                          // ACRE année 1: -50% charges
                          if (hasACRE && activeModal.exercice?.isFirstYear) {
                            chargesPatronales = Math.round(chargesPatronales * 0.5);
                          }
                          // JEI exonération
                          if (advancedOptions.jei && activeModal.exercice?.exerciceNumber <= 7) {
                            const jeiRed = activeModal.exercice.exerciceNumber <= 3 ? 0.5 :
                                           (activeModal.exercice.exerciceNumber <= 5 ? 0.3 : 0.2);
                            chargesPatronales = Math.round(chargesPatronales * (1 - jeiRed));
                          }

                          const coutTotal = annualBrut + chargesPatronales;

                          return (
                            <div key={idx} className="table-row personnel-row">
                              <div className="col-personnel-role">
                                <span className="role-name">{getRoleLabel(manager.role)}</span>
                              </div>
                              <div className="col-personnel-status">
                                <span
                                  className={`status-tag ${manager.status === 'TNS' ? 'tns' : 'as'}`}
                                  title={manager.status === 'TNS' ? 'Travailleur Non Salarié - Cotisations SSI ~45%' : 'Assimilé Salarié - Régime général ~54%'}
                                >
                                  {calcResult.statusDisplay || (manager.status === 'ASSIMILE_SALARIE' ? 'AS' : manager.status)}
                                </span>
                              </div>
                              <div className="col-personnel-brut">
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    value={manager.netMonthly || ''}
                                    onChange={(e) => {
                                      setManagers(prev => prev.map((m, i) =>
                                        i === idx ? { ...m, netMonthly: parseFloat(e.target.value) || 0 } : m
                                      ));
                                    }}
                                    min="0"
                                    step="100"
                                  />
                                  <span>€</span>
                                </div>
                              </div>
                              <div className="col-personnel-months">
                                <input
                                  type="number"
                                  value={manager.months || ''}
                                  onChange={(e) => {
                                    setManagers(prev => prev.map((m, i) =>
                                      i === idx ? { ...m, months: parseInt(e.target.value) || 0 } : m
                                    ));
                                  }}
                                  min="0"
                                  max="12"
                                  className="months-input"
                                />
                              </div>
                              <div className="col-personnel-charges">
                                <span className="charges-value">
                                  +{formatCurrency(chargesPatronales)}
                                  {hasACRE && activeModal.exercice?.isFirstYear && (
                                    <span className="tag-reduction">ACRE</span>
                                  )}
                                </span>
                                <span className="charges-rate">({Math.round(calcResult.tauxCharges * 100)}%)</span>
                              </div>
                              <div className="col-personnel-cout">
                                <strong>{formatCurrency(coutTotal)}</strong>
                              </div>
                              <div className="col-personnel-actions">
                                {managers.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    onClick={() => setManagers(prev => prev.filter((_, i) => i !== idx))}
                                    title="Supprimer ce dirigeant"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="no-data">Aucun dirigeant configuré (retournez à l'étape 2)</p>
                  )}

                  {/* Bouton ajouter dirigeant si possible */}
                  {canAddManager && addManagerLabel && (
                    <button type="button" className="btn-add-manager modal-add" onClick={addManager}>
                      + {addManagerLabel}
                    </button>
                  )}
                </div>

                {/* Section Employés - GRILLE avec mode simplifié/détaillé */}
                <div className="personnel-section">
                  <div className="section-header">
                    <h5>Employés <Tooltip text="Personnel salarié hors dirigeants. Charges patronales ~45% du brut."><span className="help-icon">ⓘ</span></Tooltip></h5>
                    <div className="header-controls">
                      <label className="toggle-checkbox">
                        <input
                          type="checkbox"
                          checked={hasEmployees}
                          onChange={(e) => setHasEmployees(e.target.checked)}
                        />
                        <span>Activer</span>
                      </label>
                      {hasEmployees && (
                        <div className="mode-toggle compact">
                          <button className={getStep3Mode() === 'simplified' ? 'active' : ''} onClick={() => setStep3Mode('simplified')}>Agrégé</button>
                          <button className={getStep3Mode() === 'detailed' ? 'active' : ''} onClick={() => setStep3Mode('detailed')}>Détaillé</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {hasEmployees && getStep3Mode() === 'simplified' && (
                    /* Mode agrégé : une ligne globale */
                    <div className="charges-table personnel-table">
                      <div className="table-header">
                        <div className="col-personnel-role">Type</div>
                        <div className="col-personnel-count">Effectif <Tooltip text="Nombre total d'employés (ETP)"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire brut mensuel moyen par employé"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">× Mois <Tooltip text="Nombre de mois de salaire par an (12 standard, 13 avec 13ème mois)"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges <Tooltip text="Charges patronales ~45% du brut"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût total <Tooltip text="Coût total = Brut × Effectif × Mois × 1.45"><span className="help-icon">ⓘ</span></Tooltip></div>
                      </div>
                      <div className="table-body">
                        <div className="table-row personnel-row">
                          <div className="col-personnel-role">
                            <span className="role-name">Tous employés</span>
                          </div>
                          <div className="col-personnel-count">
                            <input
                              type="number"
                              value={employeesData.count || ''}
                              onChange={(e) => setEmployeesData(prev => ({ ...prev, count: parseInt(e.target.value) || 0 }))}
                              min="0"
                              className="count-input"
                            />
                          </div>
                          <div className="col-personnel-brut">
                            <div className="input-with-suffix">
                              <input
                                type="number"
                                value={employeesData.avgGrossSalary || ''}
                                onChange={(e) => setEmployeesData(prev => ({ ...prev, avgGrossSalary: parseFloat(e.target.value) || 0 }))}
                                min="0"
                                step="100"
                              />
                              <span>€</span>
                            </div>
                          </div>
                          <div className="col-personnel-months">
                            <input
                              type="number"
                              value={employeesData.months || ''}
                              onChange={(e) => setEmployeesData(prev => ({ ...prev, months: parseInt(e.target.value) || 0 }))}
                              min="0"
                              max="12"
                              className="months-input"
                            />
                          </div>
                          <div className="col-personnel-charges">
                            {(() => {
                              const masseBrute = employeesData.count * employeesData.avgGrossSalary * employeesData.months;
                              const charges = Math.round(masseBrute * 0.45);
                              return (
                                <>
                                  <span className="charges-value">+{formatCurrency(charges)}</span>
                                  <span className="charges-rate">(45%)</span>
                                </>
                              );
                            })()}
                          </div>
                          <div className="col-personnel-cout">
                            <strong>{formatCurrency(Math.round(employeesData.count * employeesData.avgGrossSalary * employeesData.months * 1.45))}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {hasEmployees && getStep3Mode() === 'detailed' && (
                    /* Mode détaillé : liste individuelle des employés */
                    <div className="charges-table personnel-table detailed">
                      <div className="table-header">
                        <div className="col-personnel-name">Poste / Nom</div>
                        <div className="col-personnel-brut">Brut/mois <Tooltip text="Salaire brut mensuel de l'employé"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-months">× Mois <Tooltip text="Nombre de mois de salaire par an (12 standard, 13 avec 13ème mois)"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-count">Effectif <Tooltip text="Nombre de personnes à ce poste"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-charges">Charges <Tooltip text="Charges patronales ~45% du brut"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-cout">Coût annuel <Tooltip text="Coût total = Brut × Mois × Effectif × 1.45"><span className="help-icon">ⓘ</span></Tooltip></div>
                        <div className="col-personnel-actions"></div>
                      </div>
                      <div className="table-body">
                        {employeeProfiles.length > 0 ? (
                          employeeProfiles.map(profile => {
                            const months = profile.months || 12;
                            const annualBrut = profile.grossMonthlySalary * months * profile.headcount;
                            const charges = Math.round(annualBrut * 0.45);
                            const coutTotal = annualBrut + charges;
                            return (
                              <div key={profile.id} className="table-row personnel-row">
                                <div className="col-personnel-name">
                                  <input
                                    type="text"
                                    value={profile.profileName || ''}
                                    onChange={(e) => updateEmployeeProfile(profile.id, 'profileName', e.target.value)}
                                    placeholder="Ex: Commercial, Assistant..."
                                    className="input-name"
                                  />
                                </div>
                                <div className="col-personnel-brut">
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={profile.grossMonthlySalary || ''}
                                      onChange={(e) => updateEmployeeProfile(profile.id, 'grossMonthlySalary', parseInt(e.target.value) || 0)}
                                      min="0"
                                      step="100"
                                    />
                                    <span>€</span>
                                  </div>
                                </div>
                                <div className="col-personnel-months">
                                  <input
                                    type="number"
                                    value={months}
                                    onChange={(e) => updateEmployeeProfile(profile.id, 'months', parseInt(e.target.value) || 12)}
                                    min="0"
                                    max="12"
                                    className="months-input"
                                  />
                                </div>
                                <div className="col-personnel-count">
                                  <input
                                    type="number"
                                    value={profile.headcount || ''}
                                    onChange={(e) => updateEmployeeProfile(profile.id, 'headcount', parseInt(e.target.value) || 1)}
                                    min="1"
                                    className="count-input"
                                  />
                                </div>
                                <div className="col-personnel-charges">
                                  <span className="charges-value">+{formatCurrency(charges)}</span>
                                  <span className="charges-rate">(45%)</span>
                                </div>
                                <div className="col-personnel-cout">
                                  <strong>{formatCurrency(coutTotal)}</strong>
                                </div>
                                <div className="col-personnel-actions">
                                  <button
                                    type="button"
                                    className="btn-remove"
                                    onClick={() => removeEmployeeProfile(profile.id)}
                                    title="Supprimer"
                                  >
                                    ×
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="table-row empty-row">
                            <div className="empty-message">Aucun employé ajouté</div>
                          </div>
                        )}
                        <div className="table-row add-row">
                          <div className="add-cell">
                            <button type="button" className="btn-add-employee" onClick={addEmployeeProfile}>
                              + Ajouter un employé
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!hasEmployees && (
                    <p className="no-data muted">Pas d'employés (hors dirigeants)</p>
                  )}
                </div>

                {/* Légende et aide sur les calculs */}
                <div className="modal-legend">
                  <div className="legend-item">
                    <span className="legend-input"></span>
                    <span>Champ éditable</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-calc"></span>
                    <span>Valeur calculée automatiquement</span>
                  </div>
                </div>

                <div className="impot-rules-help">
                  <div className="rule-item">
                    <span className="rule-icon">💰</span>
                    <span><strong>Coût total</strong> = Salaire BRUT + Charges patronales</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-icon">📊</span>
                    <span><strong>TNS</strong> (Travailleur Non Salarié): ~45% | <strong>AS</strong> (Assimilé Salarié): ~54% | <strong>Employés:</strong> ~45%</span>
                  </div>
                </div>

                {/* Total */}
                {(() => {
                  const totals = calculateExerciceTotalsNew(activeModal.exercice);
                  return (
                    <div className="modal-total">
                      <span>Total compte 64 ({activeModal.exercice?.label})</span>
                      <strong>-{formatCurrency(totals.coutDirigeants + totals.coutEmployes)}</strong>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <span className="footer-hint">BRUT + Charges patronales = Coût entreprise</span>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal 61-62 Services extérieurs - ÉDITION DES CHAMPS DE BASE */}
        {activeModal?.type === 'chargeExploitation' && (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">61-62</span> Services extérieurs</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                {/* Toggle mode */}
                <div className="mode-toggle-row">
                  <span className="mode-label">Mode :</span>
                  <div className="mode-toggle">
                    <button className={getStep3Mode() === 'simplified' ? 'active' : ''} onClick={() => setStep3Mode('simplified')}>Simplifié</button>
                    <button className={getStep3Mode() === 'detailed' ? 'active' : ''} onClick={() => setStep3Mode('detailed')}>Détaillé</button>
                  </div>
                </div>

                {getStep3Mode() === 'simplified' ? (
                  /* Mode simplifié : ligne unique */
                  <div className="simple-grid">
                    <div className="grid-row header">
                      <div className="col-type">Type</div>
                      <div className="col-value">Valeur</div>
                    </div>
                    <div className={`grid-row ${!simplifiedCharges.usePercent ? 'selected' : ''}`} onClick={() => setSimplifiedCharges(prev => ({ ...prev, usePercent: false }))}>
                      <div className="col-type">
                        <input type="radio" checked={!simplifiedCharges.usePercent} readOnly />
                        <span>Montant fixe annuel</span>
                      </div>
                      <div className="col-value">
                        <input
                          type="number"
                          value={simplifiedCharges.totalFixed || ''}
                          onChange={(e) => setSimplifiedCharges(prev => ({ ...prev, totalFixed: parseFloat(e.target.value) || 0 }))}
                          placeholder="25000"
                          disabled={simplifiedCharges.usePercent}
                        />
                        <span className="unit">€/an</span>
                      </div>
                    </div>
                    <div className={`grid-row ${simplifiedCharges.usePercent ? 'selected' : ''}`} onClick={() => setSimplifiedCharges(prev => ({ ...prev, usePercent: true }))}>
                      <div className="col-type">
                        <input type="radio" checked={simplifiedCharges.usePercent} readOnly />
                        <span>Pourcentage du CA</span>
                      </div>
                      <div className="col-value">
                        <input
                          type="number"
                          value={simplifiedCharges.totalPercent || ''}
                          onChange={(e) => setSimplifiedCharges(prev => ({ ...prev, totalPercent: parseFloat(e.target.value) || 0 }))}
                          placeholder="20"
                          disabled={!simplifiedCharges.usePercent}
                        />
                        <span className="unit">%</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Mode détaillé : tableau - Type avant Montant */
                  <div className="charges-table">
                    <div className="table-header">
                      <div className="col-check"></div>
                      <div className="col-label">Poste de charge</div>
                      <div className="col-type">Type</div>
                      <div className="col-amount">Montant</div>
                    </div>
                    <div className="table-body">
                      {COMMON_CHARGE_LINES.filter(line =>
                        !['salaires_employes', 'charges_sociales_employes'].includes(line.key)
                      ).map(line => {
                        const chargeData = detailedCharges[line.key] || { enabled: false, value: line.defaultValue || 0, type: 'fixed' };
                        return (
                          <div key={line.key} className={`table-row ${chargeData.enabled ? 'enabled' : ''}`}>
                            <div className="col-check">
                              <input
                                type="checkbox"
                                checked={chargeData.enabled}
                                onChange={(e) => setDetailedCharges(prev => ({
                                  ...prev,
                                  [line.key]: { ...chargeData, enabled: e.target.checked, value: chargeData.value || line.defaultValue || 0 }
                                }))}
                              />
                            </div>
                            <div className="col-label">
                              <span className="icon">{line.icon}</span>
                              <span>{line.label}</span>
                            </div>
                            <div className="col-type">
                              <select
                                value={chargeData.type}
                                onChange={(e) => setDetailedCharges(prev => ({
                                  ...prev,
                                  [line.key]: { ...chargeData, type: e.target.value }
                                }))}
                                disabled={!chargeData.enabled}
                              >
                                <option value="fixed">€/an</option>
                                <option value="percent">% CA</option>
                              </select>
                            </div>
                            <div className="col-amount">
                              <input
                                type="number"
                                value={chargeData.enabled ? (chargeData.value || '') : ''}
                                onChange={(e) => setDetailedCharges(prev => ({
                                  ...prev,
                                  [line.key]: { ...chargeData, value: parseFloat(e.target.value) || 0 }
                                }))}
                                placeholder={String(line.defaultValue || 0)}
                                disabled={!chargeData.enabled}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Total */}
                {(() => {
                  const totals = calculateExerciceTotalsNew(activeModal.exercice);
                  return (
                    <div className="modal-total">
                      <span>Total compte 61-62</span>
                      <strong>-{formatCurrency(totals.chargesExploitation)}</strong>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <span className="footer-hint">Calcul automatique</span>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal 63 Impôts et taxes - REFONTE COMPLÈTE */}
        {activeModal?.type === 'chargeImpots' && (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">63</span> Impôts, taxes et versements</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Hypothèses modifiables → calculs automatiques selon les règles fiscales
                  {entityData.postalCode && (
                    <span className="location-tag">📍 {entityData.postalCode}{detectedCity && ` - ${detectedCity}`}</span>
                  )}
                </p>

                {/* Tableau grille des impôts */}
                <div className="charges-table impots-table">
                  <div className="table-header">
                    <div className="col-impot-label">Impôt / Taxe</div>
                    <div className="col-impot-hypothese">Hypothèses <Tooltip text="Valeurs de base modifiables servant au calcul automatique"><span className="help-icon">ⓘ</span></Tooltip></div>
                    <div className="col-impot-calcul">Formule</div>
                    <div className="col-impot-montant">Montant</div>
                  </div>
                  <div className="table-body">
                    {/* CFE - Cotisation Foncière des Entreprises */}
                    {(() => {
                      const totals = calculateExerciceTotalsNew(activeModal.exercice);
                      const ca = totals.totalCA || 0;

                      // Base minimale selon CA (plancher obligatoire)
                      const baseMinAuto = getCfeBaseMinimaleFromCA(ca);
                      const baseMinEffective = fiscalParams.cfeBaseMinimale || baseMinAuto;
                      const cotisationMin = Math.round(baseMinEffective * fiscalParams.cfeTaux / 100);

                      // Base CFE selon le mode
                      let cfeBase = 0;
                      let cfeCalculee = 0;
                      let useMin = false;

                      if (fiscalParams.cfeHasLocal) {
                        cfeBase = fiscalParams.cfeSurface * fiscalParams.cfeTarifM2;
                        const cfeFromLocal = Math.round(cfeBase * fiscalParams.cfeTaux / 100);
                        // Si CFE calculée < minimum, on applique le minimum
                        if (cfeFromLocal < cotisationMin) {
                          cfeCalculee = cotisationMin;
                          useMin = true;
                        } else {
                          cfeCalculee = cfeFromLocal;
                        }
                      } else {
                        cfeBase = baseMinEffective;
                        cfeCalculee = cotisationMin;
                      }

                      // CFE = MAX(Base × Taux, Cotisation minimum)
                      const cfeAvantExo = cfeCalculee;

                      let cfeFinal = cfeAvantExo;
                      let exoLabel = '';
                      if (activeModal.exercice?.isFirstYear) {
                        cfeFinal = 0;
                        exoLabel = 'Exo 100% (an 1)';
                      } else if (activeModal.exercice?.isSecondYear) {
                        cfeFinal = Math.round(cfeAvantExo * 0.5);
                        exoLabel = '-50% (an 2)';
                      }

                      return (
                        <div className="table-row impot-row cfe-row">
                          <div className="col-impot-label">
                            <strong>CFE</strong>
                            <span className="impot-subtitle">Cotisation Foncière des Entreprises</span>
                            <span className="impot-ca-info" style={{display: 'block', fontSize: '10px', color: '#666', marginTop: '2px'}}>
                              CA: {formatCurrency(ca)} → Min CFE: {formatCurrency(cotisationMin)}
                            </span>
                          </div>
                          <div className="col-impot-hypothese">
                            <div className="hypothesis-inputs">
                              {/* Toggle Avec/Sans local */}
                              <div className="input-row toggle-row">
                                <div className="mode-toggle compact">
                                  <button
                                    type="button"
                                    className={fiscalParams.cfeHasLocal ? 'active' : ''}
                                    onClick={() => {
                                      setFiscalParams(p => ({ ...p, cfeHasLocal: true }));
                                      // Clear CFE override when mode changes
                                      setExerciceOverrides(prev => ({
                                        ...prev,
                                        [activeModal.exercice?.key]: {
                                          ...prev[activeModal.exercice?.key],
                                          cfe: null
                                        }
                                      }));
                                    }}
                                  >
                                    Avec local
                                  </button>
                                  <button
                                    type="button"
                                    className={!fiscalParams.cfeHasLocal ? 'active' : ''}
                                    onClick={() => {
                                      setFiscalParams(p => ({ ...p, cfeHasLocal: false }));
                                      // Clear CFE override when mode changes
                                      setExerciceOverrides(prev => ({
                                        ...prev,
                                        [activeModal.exercice?.key]: {
                                          ...prev[activeModal.exercice?.key],
                                          cfe: null
                                        }
                                      }));
                                    }}
                                  >
                                    Sans local
                                  </button>
                                </div>
                              </div>

                              {fiscalParams.cfeHasLocal ? (
                                /* Mode AVEC LOCAL - valeur locative estimée */
                                <>
                                  <div className="input-row">
                                    <label>Surface <Tooltip text="Surface des locaux professionnels"><span className="help-icon">ⓘ</span></Tooltip></label>
                                    <div className="input-with-suffix">
                                      <input
                                        type="number"
                                        value={fiscalParams.cfeSurface || ''}
                                        onChange={(e) => setFiscalParams(p => ({ ...p, cfeSurface: parseFloat(e.target.value) || 0 }))}
                                        min="0"
                                        step="5"
                                      />
                                      <span>m²</span>
                                    </div>
                                  </div>
                                  <div className="input-row">
                                    <label>Tarif/m² <Tooltip text="Tarif cadastral au m² (10-25€ selon zone)"><span className="help-icon">ⓘ</span></Tooltip></label>
                                    <div className="input-with-suffix">
                                      <input
                                        type="number"
                                        value={fiscalParams.cfeTarifM2 || ''}
                                        onChange={(e) => setFiscalParams(p => ({ ...p, cfeTarifM2: parseFloat(e.target.value) || 0 }))}
                                        min="0"
                                        max="50"
                                        step="1"
                                      />
                                      <span>€</span>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                /* Mode SANS LOCAL - base minimale */
                                <div className="input-row">
                                  <label>Base minimale <Tooltip text={`Base fixée par la commune selon CA. Auto: ${formatCurrency(baseMinAuto)}`}><span className="help-icon">ⓘ</span></Tooltip></label>
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={fiscalParams.cfeBaseMinimale || ''}
                                      onChange={(e) => setFiscalParams(p => ({ ...p, cfeBaseMinimale: parseFloat(e.target.value) || null }))}
                                      placeholder={baseMinAuto}
                                      min="0"
                                      step="100"
                                    />
                                    <span>€</span>
                                  </div>
                                </div>
                              )}

                              <div className="input-row">
                                <label>Taux communal <Tooltip text="Taux fixé par la commune (20-40%)"><span className="help-icon">ⓘ</span></Tooltip></label>
                                <div className="input-with-suffix">
                                  <input
                                    type="number"
                                    value={fiscalParams.cfeTaux || ''}
                                    onChange={(e) => setFiscalParams(p => ({ ...p, cfeTaux: parseFloat(e.target.value) || 0 }))}
                                    min="0"
                                    max="50"
                                    step="0.5"
                                  />
                                  <span>%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-impot-calcul">
                            <div className="calcul-stack">
                              {fiscalParams.cfeHasLocal ? (
                                <>
                                  <span className="calcul-formula">{fiscalParams.cfeSurface}m² × {fiscalParams.cfeTarifM2}€ = {formatCurrency(cfeBase)}</span>
                                  <span className="calcul-formula">{formatCurrency(cfeBase)} × {fiscalParams.cfeTaux}% = {formatCurrency(Math.round(cfeBase * fiscalParams.cfeTaux / 100))}</span>
                                  {useMin && (
                                    <span className="calcul-note warning">→ Min. {formatCurrency(cotisationMin)} (CA {formatCurrency(ca)})</span>
                                  )}
                                </>
                              ) : (
                                <>
                                  <span className="calcul-formula">Base min: {formatCurrency(cfeBase)} (CA {formatCurrency(ca)})</span>
                                  <span className="calcul-formula">{formatCurrency(cfeBase)} × {fiscalParams.cfeTaux}% = {formatCurrency(cfeAvantExo)}</span>
                                </>
                              )}
                              {exoLabel && <span className="calcul-formula positive">{exoLabel}</span>}
                            </div>
                          </div>
                          <div className="col-impot-montant">
                            <div className="editable-amount">
                              <input
                                type="number"
                                value={exerciceOverrides[activeModal.exercice?.key]?.cfe ?? cfeFinal}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  setExerciceOverrides(prev => ({
                                    ...prev,
                                    [activeModal.exercice?.key]: {
                                      ...prev[activeModal.exercice?.key],
                                      cfe: isNaN(val) ? null : val
                                    }
                                  }));
                                }}
                                className="amount-input"
                              />
                              <span className="currency">€</span>
                              {exerciceOverrides[activeModal.exercice?.key]?.cfe != null && (
                                <button
                                  type="button"
                                  className="btn-reset"
                                  onClick={() => setExerciceOverrides(prev => ({
                                    ...prev,
                                    [activeModal.exercice?.key]: {
                                      ...prev[activeModal.exercice?.key],
                                      cfe: null
                                    }
                                  }))}
                                  title="Réinitialiser au calcul auto"
                                >↺</button>
                              )}
                            </div>
                            {exerciceOverrides[activeModal.exercice?.key]?.cfe == null && (
                              <span className="calc-indicator">auto</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* CVAE - Cotisation sur la Valeur Ajoutée */}
                    {(() => {
                      const totals = calculateExerciceTotalsNew(activeModal.exercice);
                      const ca = totals.totalProduits || 0;
                      const isApplicable = ca >= 500000;

                      // Taux CVAE progressif selon CA
                      let tauxCVAE = 0;
                      if (ca >= 500000 && ca < 3000000) tauxCVAE = 0.25;
                      else if (ca >= 3000000 && ca < 10000000) tauxCVAE = 0.45;
                      else if (ca >= 10000000 && ca < 50000000) tauxCVAE = 0.55;
                      else if (ca >= 50000000) tauxCVAE = 0.75;

                      const va = Math.round(ca * fiscalParams.cvaeVAPercent / 100);
                      const cvaeAmount = isApplicable ? Math.round(va * tauxCVAE / 100) : 0;

                      return (
                        <div className={`table-row impot-row ${!isApplicable ? 'disabled' : ''}`}>
                          <div className="col-impot-label">
                            <strong>CVAE</strong>
                            <span className="impot-subtitle">Cotisation sur la Valeur Ajoutée</span>
                          </div>
                          <div className="col-impot-hypothese">
                            {isApplicable ? (
                              <div className="hypothesis-inputs">
                                <div className="input-row">
                                  <label>VA en % du CA <Tooltip text="Valeur ajoutée = CA - achats - services externes"><span className="help-icon">ⓘ</span></Tooltip></label>
                                  <div className="input-with-suffix">
                                    <input
                                      type="number"
                                      value={fiscalParams.cvaeVAPercent || ''}
                                      onChange={(e) => setFiscalParams(p => ({ ...p, cvaeVAPercent: parseFloat(e.target.value) || 0 }))}
                                      min="0"
                                      max="100"
                                    />
                                    <span>%</span>
                                  </div>
                                </div>
                                <span className="info-line">VA estimée: {formatCurrency(va)}</span>
                              </div>
                            ) : (
                              <span className="not-applicable">CA &lt; 500 000 € → Non due</span>
                            )}
                          </div>
                          <div className="col-impot-calcul">
                            {isApplicable ? (
                              <span className="calcul-formula">{formatCurrency(va)} × {tauxCVAE}%</span>
                            ) : (
                              <span className="calcul-formula muted">—</span>
                            )}
                          </div>
                          <div className="col-impot-montant">
                            <div className="editable-amount">
                              <input
                                type="number"
                                value={exerciceOverrides[activeModal.exercice?.key]?.cvae ?? cvaeAmount}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  setExerciceOverrides(prev => ({
                                    ...prev,
                                    [activeModal.exercice?.key]: {
                                      ...prev[activeModal.exercice?.key],
                                      cvae: isNaN(val) ? null : val
                                    }
                                  }));
                                }}
                                className="amount-input"
                                disabled={!isApplicable}
                              />
                              <span className="currency">€</span>
                              {exerciceOverrides[activeModal.exercice?.key]?.cvae != null && (
                                <button
                                  type="button"
                                  className="btn-reset"
                                  onClick={() => setExerciceOverrides(prev => ({
                                    ...prev,
                                    [activeModal.exercice?.key]: {
                                      ...prev[activeModal.exercice?.key],
                                      cvae: null
                                    }
                                  }))}
                                  title="Réinitialiser au calcul auto"
                                >↺</button>
                              )}
                            </div>
                            {exerciceOverrides[activeModal.exercice?.key]?.cvae == null && isApplicable && (
                              <span className="calc-indicator">auto</span>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Aide sur les règles */}
                <div className="impot-rules-help">
                  <div className="rule-item">
                    <span className="rule-icon">🏢</span>
                    <span><strong>IS:</strong> 15% jusqu'à 42 500€ de bénéfice, 25% au-delà</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-icon">📋</span>
                    <span><strong>CFE:</strong> Base × Taux communal. Exo 100% an 1, -50% an 2</span>
                  </div>
                  <div className="rule-item">
                    <span className="rule-icon">📊</span>
                    <span><strong>CVAE:</strong> Due si CA ≥ 500k€. Taux progressif 0.25% à 0.75%</span>
                  </div>
                </div>

                {/* Total */}
                {(() => {
                  const totals = calculateExerciceTotalsNew(activeModal.exercice);
                  return (
                    <div className="modal-total">
                      <span>Total taxes ({activeModal.exercice?.label})</span>
                      <strong>-{formatCurrency(totals.totalImpotsEtTaxes)}</strong>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <span className="footer-hint">Calculs automatiques selon les règles fiscales</span>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal détail Achats (60) - EDITABLE */}
        {activeModal?.type === 'chargeAchats' && (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">60</span> Achats et charges variables</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Configurez les charges liées à chaque source de CA - {activeModal.exercice?.label}
                </p>

                {(() => {
                  const data = getExerciceDataForYear(activeModal.exerciceKey);
                  const cvPrincipal = data.caPrincipal.chargesVariables || { type: 'percent', value: 0 };
                  const montantPrincipal = cvPrincipal.type === 'percent'
                    ? Math.round((data.caPrincipal.amount || 0) * (cvPrincipal.value || 0) / 100)
                    : (cvPrincipal.value || 0);

                  let totalCharges = montantPrincipal;
                  data.caSecondaires.forEach(source => {
                    const cv = source.chargesVariables || { type: 'fixed', value: 0 };
                    totalCharges += cv.type === 'percent'
                      ? Math.round((source.amount || 0) * (cv.value || 0) / 100)
                      : (cv.value || 0);
                  });

                  return (
                    <>
                      {/* CA Principal */}
                      <div className="charge-detail-section">
                        <h5>CA Principal : {data.caPrincipal.label || 'Activité principale'}</h5>
                        <div className="charges-config-row">
                          <span className="config-label">CA : {formatCurrency(data.caPrincipal.amount || 0)}</span>
                          <div className="config-inputs">
                            <select
                              value={cvPrincipal.type}
                              onChange={(e) => updateCAPrincipal(activeModal.exerciceKey, {
                                chargesVariables: { ...cvPrincipal, type: e.target.value }
                              })}
                            >
                              <option value="percent">% du CA</option>
                              <option value="fixed">Montant fixe</option>
                            </select>
                            <input
                              type="number"
                              value={cvPrincipal.value || ''}
                              onChange={(e) => updateCAPrincipal(activeModal.exerciceKey, {
                                chargesVariables: { ...cvPrincipal, value: parseFloat(e.target.value) || 0 }
                              })}
                              placeholder="0"
                              min="0"
                              step={cvPrincipal.type === 'percent' ? '1' : '100'}
                            />
                            <span className="suffix">{cvPrincipal.type === 'percent' ? '%' : '€'}</span>
                          </div>
                          <span className="config-result">= {formatCurrency(montantPrincipal)}</span>
                        </div>
                      </div>

                      {/* CA Secondaires */}
                      {data.caSecondaires.length > 0 && (
                        <div className="charge-detail-section">
                          <h5>Activités secondaires</h5>
                          {data.caSecondaires.map(source => {
                            const cv = source.chargesVariables || { type: 'fixed', value: 0 };
                            const montant = cv.type === 'percent'
                              ? Math.round((source.amount || 0) * (cv.value || 0) / 100)
                              : (cv.value || 0);
                            return (
                              <div key={source.id} className="charges-config-row">
                                <span className="config-label">
                                  {source.label || 'Secondaire'} : {formatCurrency(source.amount || 0)}
                                </span>
                                <div className="config-inputs">
                                  <select
                                    value={cv.type}
                                    onChange={(e) => updateCASecondaire(activeModal.exerciceKey, source.id, {
                                      chargesVariables: { ...cv, type: e.target.value }
                                    })}
                                  >
                                    <option value="percent">% du CA</option>
                                    <option value="fixed">Montant fixe</option>
                                  </select>
                                  <input
                                    type="number"
                                    value={cv.value || ''}
                                    onChange={(e) => updateCASecondaire(activeModal.exerciceKey, source.id, {
                                      chargesVariables: { ...cv, value: parseFloat(e.target.value) || 0 }
                                    })}
                                    placeholder="0"
                                    min="0"
                                    step={cv.type === 'percent' ? '1' : '100'}
                                  />
                                  <span className="suffix">{cv.type === 'percent' ? '%' : '€'}</span>
                                </div>
                                <span className="config-result">= {formatCurrency(montant)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {data.caSecondaires.length === 0 && (
                        <p className="info-text">
                          Ajoutez des activités secondaires (+ CA) pour leur affecter des charges spécifiques.
                        </p>
                      )}

                      {/* Total */}
                      <div className="charge-total-row">
                        <span>Total compte 60 :</span>
                        <strong className="neg">-{formatCurrency(totalCharges)}</strong>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <span className="footer-hint">Ex: achats marchandises, sous-traitance, commissions</span>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal récapitulatif charges (mode simplifié) */}
        {activeModal?.type === 'chargeSummary' && (
          <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
            <div className="charges-var-modal wide" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h4><span className="pcg-badge">6</span> Récapitulatif des charges</h4>
                <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-hint">
                  Détail des charges calculées pour {activeModal.exercice?.label}
                </p>

                {(() => {
                  const totals = calculateExerciceTotalsNew(activeModal.exercice);
                  return (
                    <table className="charge-detail-table summary">
                      <thead>
                        <tr>
                          <th>Compte</th>
                          <th>Catégorie</th>
                          <th>Montant</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><span className="pcg-num">60</span></td>
                          <td>Achats (charges variables)</td>
                          <td className="neg">{formatCurrency(totals.totalChargesVar)}</td>
                        </tr>
                        <tr>
                          <td><span className="pcg-num">61-62</span></td>
                          <td>Services extérieurs</td>
                          <td className="neg">{formatCurrency(totals.chargesExploitation)}</td>
                        </tr>
                        <tr>
                          <td><span className="pcg-num">63</span></td>
                          <td>
                            Impôts, taxes
                            {activeModal.exercice?.isFirstYear && <span className="mini-tag exo">CFE exo</span>}
                            {activeModal.exercice?.isSecondYear && <span className="mini-tag reduc">CFE -50%</span>}
                            {totals.cvae > 0 && <span className="mini-tag cvae">+CVAE</span>}
                          </td>
                          <td className="neg">{formatCurrency(totals.totalImpotsEtTaxes)}</td>
                        </tr>
                        <tr>
                          <td><span className="pcg-num">64</span></td>
                          <td>Charges de personnel</td>
                          <td className="neg">{formatCurrency(totals.coutDirigeants + totals.coutEmployes)}</td>
                        </tr>
                        <tr className="total">
                          <td></td>
                          <td><strong>Total charges</strong></td>
                          <td className="neg"><strong>{formatCurrency(totals.chargesGlobales)}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}

                <p className="info-text">
                  Passez en mode "Détaillé" pour modifier chaque catégorie individuellement.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => { toggleStep3Mode(); setActiveModal(null); }}>
                  Passer en mode détaillé
                </button>
                <div className="footer-buttons">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                  <button className="btn-save" onClick={() => setActiveModal(null)}>Enregistrer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal détail affectation du résultat */}
        {activeModal?.type === 'affectationDetail' && (() => {
          const { exercice, affectation } = activeModal;
          return (
            <div className="charges-var-modal-overlay" onClick={() => setActiveModal(null)}>
              <div className="charges-var-modal affectation-detail-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h4>Affectation du résultat - {exercice.label}</h4>
                  <button className="close-btn" onClick={() => setActiveModal(null)}>×</button>
                </div>
                <div className="modal-body">
                  {/* Calcul du distribuable */}
                  <div className="affectation-section">
                    <h5>Calcul du distribuable</h5>
                    <div className="affectation-detail-grid">
                      <div className="detail-row">
                        <span className="detail-label">📊 Résultat Net</span>
                        <span className="detail-value">{formatCurrency(affectation.resultatNet)}</span>
                      </div>
                      <div className="detail-row indent">
                        <span className="detail-label">📋 Réserve légale (5%)</span>
                        <span className="detail-value neg">-{formatCurrency(affectation.reserveLegale)}</span>
                      </div>
                      <div className="detail-row sub-hint">
                        <span className="detail-label-small">Plafonné à 10% du capital ({formatCurrency(affectation.capitalSocial)})</span>
                      </div>
                      {affectation.reportNMoins1 > 0 && (
                        <div className="detail-row indent">
                          <span className="detail-label">➕ Report N-1</span>
                          <span className="detail-value pos">+{formatCurrency(affectation.reportNMoins1)}</span>
                        </div>
                      )}
                      <div className="detail-row total highlight-green">
                        <span className="detail-label"><strong>Distribuable</strong></span>
                        <span className="detail-value">{formatCurrency(affectation.distribuable)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Décision AG */}
                  <div className="affectation-section">
                    <h5>Décision AG du {affectation.agDateFormatted}</h5>
                    <p className="section-hint">Distribution à {affectation.percent}% du distribuable</p>
                    <div className="affectation-detail-grid">
                      <div className="detail-row highlight-green">
                        <span className="detail-label">💰 Dividendes ({affectation.percent}%)</span>
                        <span className="detail-value">{formatCurrency(affectation.dividendes)}</span>
                      </div>
                      <div className="detail-row sub-hint">
                        <span className="detail-label-small">Versement prévu: {affectation.paymentDateFormatted} → IR {affectation.taxYear}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">➡️ Report à nouveau</span>
                        <span className="detail-value">{formatCurrency(affectation.reportNouveau)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Note explicative */}
                  <div className="affectation-note">
                    <span className="note-icon">ℹ️</span>
                    <span>Le report à nouveau sera ajouté au distribuable de l'exercice suivant lors de la prochaine AG.</span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setActiveModal(null)}>Fermer</button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // ==================== RENDER STEP 4: SUMMARY ====================

  // Get active advanced options for display
  const getActiveAdvancedOptions = () => {
    const options = [];
    if (advancedOptions.jei) options.push('JEI');
    if (advancedOptions.cir) options.push(`CIR (${formatCurrency(advancedOptions.cirAmount)} R&D)`);
    return options;
  };

  // Get charges summary text
  const getChargesSummary = () => {
    if (chargeMode === 'simplified') {
      if (simplifiedCharges.usePercent) {
        return `${simplifiedCharges.totalPercent || 20}% du CA`;
      }
      return formatCurrency(simplifiedCharges.totalFixed || 0);
    } else {
      const enabledCharges = Object.entries(detailedCharges)
        .filter(([_, data]) => data.enabled)
        .map(([key, _]) => {
          const line = COMMON_CHARGE_LINES.find(l => l.key === key);
          return line ? line.label : key;
        });
      return enabledCharges.length > 0 ? enabledCharges.join(', ') : 'Aucune';
    }
  };

  const renderSummaryStep = () => {
    const activeAdvancedOptions = getActiveAdvancedOptions();

    return (
      <div className="step-content summary-step">
        {/* Section Identite */}
        <div className="summary-section">
          <h4>Identite</h4>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Societe</span>
              <strong className="summary-value">
                {entityData.name || 'Ma Societe'}
                <span className="badge legal">{entityData.legalForm}</span>
                <span className={`badge manager ${managerType === 'TNS' ? 'tns' : managerType === 'ASSIMILE_SALARIE' ? 'as' : 'none'}`}>
                  {managerType === 'TNS' ? 'TNS' : managerType === 'ASSIMILE_SALARIE' ? 'AS' : '—'}
                </span>
              </strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Regime fiscal</span>
              <strong className="summary-value">
                <span className={`badge tax ${entityData.taxRegime.toLowerCase()}`}>{entityData.taxRegime}</span>
              </strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Type activite</span>
              <strong className="summary-value">{entityData.activityType}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Capital</span>
              <strong className="summary-value">{formatCurrency(entityData.shareCapital)}</strong>
            </div>
            <div className="summary-item">
              <span className="summary-label">Cloture exercice</span>
              <strong className="summary-value">{fiscalYearEnd}</strong>
            </div>
            {entityData.apeCode && (
              <div className="summary-item">
                <span className="summary-label">Code APE</span>
                <strong className="summary-value">{entityData.apeCode}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Section Remuneration dirigeants (multi-managers) */}
        <div className="summary-section">
          <h4>Remuneration dirigeant{managersWithStatus.length > 1 ? 's' : ''}</h4>
          {managersWithStatus.map((manager, index) => {
            const annualNet = manager.netMonthly * manager.months;
            const calc = calculateManagerSocialCharges(annualNet, manager.status);
            return (
              <div key={manager.id} className="summary-manager-row">
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">
                      {manager.role === 'president' ? 'Président' :
                       manager.role === 'dg' ? 'Directeur Général' :
                       manager.role === 'gerant' ? 'Gérant' : 'Co-gérant'}
                      <span className={`badge manager ${manager.status === 'TNS' ? 'tns' : 'as'}`}>
                        {manager.status === 'TNS' ? 'TNS' : 'AS'}
                      </span>
                    </span>
                    <strong className="summary-value highlight">{formatCurrency(annualNet)}/an</strong>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Charges sociales</span>
                    <strong className="summary-value negative">{formatCurrency(calc.charges)}</strong>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Coût société</span>
                    <strong className="summary-value total">{formatCurrency(calc.cout)}</strong>
                  </div>
                </div>
                {index < managersWithStatus.length - 1 && <hr className="manager-separator" />}
              </div>
            );
          })}
          {hasACRE && (
            <div className="summary-option-tag">
              <span className="badge option">ACRE : -50% charges année 1</span>
            </div>
          )}
        </div>

        {/* Section Charges exploitation */}
        <div className="summary-section">
          <h4>Charges d'exploitation</h4>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Mode</span>
              <strong className="summary-value">
                <span className={`badge mode ${chargeMode}`}>
                  {chargeMode === 'simplified' ? 'Simplifie' : 'Detaille'}
                </span>
              </strong>
            </div>
            <div className="summary-item wide">
              <span className="summary-label">Montant / Details</span>
              <strong className="summary-value">{getChargesSummary()}</strong>
            </div>
          </div>
        </div>

        {/* Section Options avancees (si activees) */}
        {activeAdvancedOptions.length > 0 && (
          <div className="summary-section">
            <h4>Options avancees</h4>
            <div className="summary-tags">
              {activeAdvancedOptions.map((opt, i) => (
                <span key={i} className="badge option">{opt}</span>
              ))}
            </div>
          </div>
        )}

        {/* Section Dividendes */}
        <div className="summary-section">
          <h4>Distribution</h4>
          <div className="summary-grid">
            <div className="summary-item">
              <span className="summary-label">Dividendes</span>
              <strong className="summary-value">{dividendPercent}% du resultat net</strong>
            </div>
          </div>
        </div>

        {/* Section Sources CA */}
        <div className="summary-section">
          <h4>Sources de CA ({caSources.length})</h4>
          {caSources.length > 0 ? (
            <div className="sources-summary-list">
              {caSources.map((source, index) => {
                const typeInfo = getSourceTypeInfo(source.sourceType);
                const exercices = getExercicesForSource(source);
                return (
                  <div key={source.id} className="source-summary-item">
                    <span className="source-icon" style={{ background: typeInfo.color }}>{typeInfo.icon}</span>
                    <span className="source-info">
                      <strong>{source.label || typeInfo.label}</strong>
                      <span className="source-dates">{source.startDate} → {source.endDate}</span>
                      {exercices.length > 0 && (
                        <span className="source-exercices">
                          {exercices.map(ex => ex.label).join(', ')}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-sources-warning">
              Aucune source de CA definie
            </div>
          )}
        </div>

        {/* Apercu des exercices (si sources definies) */}
        {coveredExercices.length > 0 && (
          <div className="summary-section exercices-preview">
            <h4>Apercu des exercices</h4>
            <div className="exercices-grid">
              {coveredExercices.map(ex => {
                const totals = calculateExerciceTotals(ex.key, ex.year);
                return (
                  <div key={ex.key} className="exercice-card">
                    <div className="exercice-header">
                      <span className="ex-label">{ex.label}</span>
                      <span className="ex-dates">{ex.startDate} → {ex.endDate}</span>
                      {totals.isFirstYear && <span className="year-badge first">1ère année</span>}
                      {totals.isSecondYear && <span className="year-badge second">2e année</span>}
                    </div>
                    <div className="exercice-body">
                      <div className="pl-row">
                        <span>Chiffre d'affaires</span>
                        <span>{formatCurrency(totals.ca)}</span>
                      </div>
                      <div className="pl-row indent">
                        <span>
                          Charges exploitation
                          {totals.cfe === 0 && <span className="tag-exo">CFE exo</span>}
                          {totals.isSecondYear && totals.cfe > 0 && <span className="tag-reduc">CFE -50%</span>}
                        </span>
                        <span className="neg">{formatCurrency(totals.chargesExploitation)}</span>
                      </div>
                      <div className="pl-row indent">
                        <span>
                          Coût dirigeant{totals.managersCount > 1 ? 's' : ''}
                          {totals.acreApplied && <span className="tag-exo">ACRE</span>}
                          {totals.jeiApplied && <span className="tag-exo">JEI</span>}
                        </span>
                        <span className="neg">{formatCurrency(totals.coutDirigeants)}</span>
                      </div>
                      {totals.coutEmployes > 0 && (
                        <div className="pl-row indent">
                          <span>
                            Coût employés
                            {totals.jeiApplied && <span className="tag-exo">JEI</span>}
                          </span>
                          <span className="neg">{formatCurrency(totals.coutEmployes)}</span>
                        </div>
                      )}
                      <div className="pl-row sub-total">
                        <span>Resultat avant IS</span>
                        <span>{formatCurrency(totals.resultatBrut)}</span>
                      </div>
                      {entityData.taxRegime === 'IS' && (
                        <div className="pl-row">
                          <span>
                            IS
                            {totals.jeiApplied && totals.exerciceNumber <= 3 && <span className="tag-exo">Exo JEI</span>}
                            {totals.jeiApplied && totals.exerciceNumber > 3 && totals.exerciceNumber <= 5 && <span className="tag-reduc">-50% JEI</span>}
                            {totals.cirCredit > 0 && <span className="tag-exo">CIR -{formatCurrency(totals.cirCredit)}</span>}
                          </span>
                          <span className="neg">{formatCurrency(totals.is)}</span>
                        </div>
                      )}
                      <div className="pl-row total">
                        <span>Resultat net</span>
                        <strong>{formatCurrency(totals.resultatNet)}</strong>
                      </div>
                      <div className="pl-row">
                        <span>Dividendes ({dividendPercent}%)</span>
                        <span>{formatCurrency(totals.dividendes)}</span>
                      </div>
                    </div>
                    <div className="exercice-footer">
                      <span>≈ {formatCurrency(Math.round(totals.resultatNet / 12))}/mois</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bouton Creer la societe (bien visible) */}
        <div className="summary-action">
          <button
            className="create-entity-btn"
            onClick={handleSave}
            disabled={isSaving || !isStep1Valid}
          >
            {isSaving ? 'Creation en cours...' : 'Creer la societe'}
          </button>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="wizard-add-entity">
      <div className="entity-topbar">
        <h3>Nouvelle Société</h3>
        <div className="steps-indicator">
          {STEPS.map((step, i) => (
            <div
              key={step.id}
              className={`step-indicator ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}
              onClick={() => i <= currentStep && setCurrentStep(i)}
            >
              <span className="step-num">{step.icon}</span>
              <span className="step-label">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="topbar-actions">
          <button className="save-btn" onClick={handleSave} disabled={isSaving || !isStep1Valid}>
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="entity-body">
        {currentStep === 0 && renderIdentityStep()}
        {currentStep === 1 && renderChargesStep()}
        {currentStep === 2 && renderCAStep()}
        {currentStep === 3 && renderSummaryStep()}
      </div>

      <div className="wizard-footer">
        <button className="nav-btn" onClick={goBack} disabled={currentStep === 0}>Precedent</button>
        <div className="step-center">
          {getStepWarning() && (
            <div className="step-warning">{getStepWarning()}</div>
          )}
          <div className="step-dots">
            {STEPS.map((_, i) => <span key={i} className={`dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`} />)}
          </div>
        </div>
        {currentStep < STEPS.length - 1 ? (
          <button className="nav-btn primary" onClick={goNext} disabled={!canGoNext()}>Suivant</button>
        ) : (
          <button className="nav-btn primary" onClick={handleSave} disabled={isSaving}>Creer</button>
        )}
      </div>

      {showDetailModal && selectedExercice && (
        <ExerciceDetailModal
          year={selectedExercice.year}
          ca={selectedExercice.ca}
          // Dirigeants avec calculs
          managers={managersWithStatus.map(m => ({
            role: m.role,
            status: m.status,
            net: m.netMonthly * m.months,
            charges: calculateManagerSocialCharges(m.netMonthly * m.months, m.status).charges,
            cout: calculateManagerSocialCharges(m.netMonthly * m.months, m.status).cout
          }))}
          // Employés
          hasEmployees={hasEmployees}
          employeesMode={chargeMode}
          employeesSimplified={employeesData}
          employeeProfiles={employeeProfiles}
          // Charges
          chargeMode={chargeMode}
          simplifiedCharges={simplifiedCharges}
          exploitationLines={detailedCharges}
          // Options
          hasACRE={hasACRE}
          hasCGA={hasCGA}
          advancedOptions={advancedOptions}
          cfeEstimate={cfeEstimate}
          // Contexte
          exerciceNumber={entityData.creationDate
            ? selectedExercice.year - new Date(entityData.creationDate).getFullYear() + 1
            : 1}
          taxRegime={entityData.taxRegime}
          entityInfo={{
            name: entityData.name,
            legalForm: entityData.legalForm
          }}
          // Overrides
          initialOverrides={exerciceOverrides[selectedExercice.key] || {}}
          onSave={handleExerciceDetailSave}
          onClose={() => { setShowDetailModal(false); setSelectedExercice(null); }}
        />
      )}

      {/* Modal Warning IS irréversible pour SCI/SNC */}
      {showISWarning && (
        <div className="modal-overlay is-warning-overlay">
          <div className="modal-content is-warning-modal">
            <div className="warning-icon">⚠️</div>
            <h3>Attention : Option irréversible</h3>
            <p>
              Le passage à l'<strong>Impôt sur les Sociétés (IS)</strong> est
              <strong> définitif et irréversible</strong> pour les sociétés civiles ({entityData.legalForm}).
            </p>
            <p className="warning-detail">
              Une fois cette option exercée, vous ne pourrez plus revenir à l'Impôt sur le Revenu (IR).
              Cette décision a des conséquences fiscales importantes sur :
            </p>
            <ul className="warning-list">
              <li>La taxation des bénéfices (25% au lieu du barème progressif)</li>
              <li>La distribution des dividendes (double imposition)</li>
              <li>Les plus-values immobilières (régime des plus-values professionnelles)</li>
            </ul>

            <label className="warning-checkbox">
              <input
                type="checkbox"
                checked={isWarningConfirmed}
                onChange={(e) => setIsWarningConfirmed(e.target.checked)}
              />
              <span>Je comprends que ce choix est définitif et irréversible</span>
            </label>

            <div className="warning-actions">
              <button className="btn-cancel" onClick={cancelISSelection}>
                Annuler
              </button>
              <button
                className="btn-confirm"
                onClick={confirmISSelection}
                disabled={!isWarningConfirmed}
              >
                Confirmer le passage à l'IS
              </button>
            </div>
          </div>
        </div>
      )}

      {/* General Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={hideConfirm}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        details={confirmState.details}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </div>
  );
};

export default WizardAddEntity;
// Force rebuild $(date)
